<template>
  <div class="dashboard-viz">
    <page-header></page-header>
    <div v-if="loadErrors.length" class="load-error-banner">
      <font-awesome-icon icon="triangle-exclamation" />
      Some data could not be loaded: {{ loadErrors.join(', ') }}
    </div>

    <div class="dashboard-body">
      <!-- Sidebar -->
      <aside class="dashboard-sidebar">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="sidebar-item"
          :class="{ active: activeTab === tab.key }"
          @click="selectTab(tab.key)"
        >
          <font-awesome-icon :icon="tab.icon" class="sidebar-icon" />
          {{ tab.label }}
        </button>
      </aside>

      <!-- Main content -->
      <main class="dashboard-main">

        <!-- Overview -->
        <Transition name="tab-fade">
        <section v-show="activeTab === 'overview'" class="dashboard-section">
          <h2>Overview</h2>
          <p class="overview-intro">A quick look at every section — click a card to jump straight in.</p>
          <div class="insight-grid">
            <insight-card
              :title="`${$labels.product}s`"
              icon="cubes"
              :loading="loading.products || loading.tasks"
              :stats="[
                { label: 'Total', value: totalProducts },
                { label: 'Without tasks', value: productsWithoutTasksCount, tone: productsWithoutTasksCount > 0 ? 'warning' : 'good' },
              ]"
              @open="selectTab('products')"
            />
            <insight-card
              :title="`${$labels.project}s`"
              icon="sitemap"
              :loading="loading.projects || entriesLoading"
              :stats="[
                { label: 'Active', value: activeProjectsCount },
                { label: 'Behind pace', value: behindPaceCount, tone: behindPaceCount > 0 ? 'critical' : 'good' },
              ]"
              @open="selectTab('projects')"
            />
            <insight-card
              title="Users"
              icon="users"
              :loading="loading.users"
              :stats="[
                { label: 'Trainees', value: traineeCount },
                { label: 'Inactive', value: inactiveUsersCount, tone: inactiveUsersCount > 0 ? 'warning' : 'good' },
              ]"
              @open="selectTab('users')"
            />
            <insight-card
              title="Tasks"
              icon="tasks"
              :loading="loading.tasks"
              :stats="[
                { label: 'Total', value: totalTasks },
                { label: 'Chapters covered', value: chaptersCoveredCount },
              ]"
              @open="selectTab('tasks')"
            />
            <insight-card
              title="Logbook Entries"
              icon="book-open"
              :loading="loading.logbooks || entriesLoading"
              :stats="[
                { label: 'Completed', value: completedEntriesCount },
                { label: 'At-risk trainees', value: atRiskLogbooks.length, tone: atRiskLogbooks.length > 0 ? 'critical' : 'good' },
              ]"
              @open="selectTab('logbooks')"
            />
            <insight-card
              title="Evaluations"
              icon="clipboard-check"
              :loading="loading.projects || evaluationsLoading"
              :stats="[
                { label: 'Passed', value: passedEvaluationsCount },
                { label: 'Needs attention', value: evaluationsNeedingAttention.length, tone: evaluationsNeedingAttention.length > 0 ? 'critical' : 'good' },
              ]"
              @open="selectTab('evaluations')"
            />
          </div>

          <div class="chart-row" style="margin-top: 20px;">
            <line-chart
              title="Activity in the last 30 days"
              :series="signOffTrendSeries"
              :buckets="recentActivityTrend.buckets"
              note="Trainee completions vs. instructor sign-offs"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No sign-off activity recorded yet."
              @point-click="drillSignOffBucket"
            />
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <recent-activity-feed
              title="Recent activity"
              note="Completions, sign-offs & evaluation outcomes"
              :items="recentActivityFeed"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No activity recorded yet."
              @item-click="handleActivityClick"
            />
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <recent-activity-feed
              title="Recently added tasks"
              :note="`${$labels.product} catalog`"
              :items="recentTasksAdded"
              :loading="loading.products || loading.tasks"
              empty-text="No tasks recorded yet."
              @item-click="handleActivityClick"
            />
          </div>
        </section>
        </Transition>

        <!-- Products -->
        <Transition name="tab-fade">
        <section v-show="activeTab === 'products'" class="dashboard-section">
          <h2>{{ $labels.product }}s</h2>
          <div class="kpi-row">
            <stat-tile :label="`Total ${$labels.product}s`" :value="totalProducts" :loading="loading.products" icon="cubes" />
            <stat-tile label="With tasks" :value="productsWithTasksCount" :loading="loading.products || loading.tasks" icon="circle-check" tone="good" />
            <stat-tile label="Without tasks" :value="productsWithoutTasksCount" :loading="loading.products || loading.tasks" icon="triangle-exclamation" tone="warning" />
            <stat-tile label="Total tasks" :value="totalTasks" :loading="loading.tasks" icon="list-check" tone="neutral" />
            <stat-tile label="Avg. pass rate" :value="avgProductPassRateDisplay" :loading="loading.products" icon="percent" tone="neutral" />
          </div>
          <div class="chart-row">
            <bar-chart
              :title="`${$labels.product}s by task count`"
              :items="topProductsByTasks"
              :loading="loading.products || loading.tasks"
              empty-text="No tasks recorded yet."
              @bar-click="drillProductTasks"
            />
            <stacked-bar
              title="Task coverage"
              :segments="productCoverageSegments"
              :loading="loading.products || loading.tasks"
              empty-text="No products recorded yet."
              @segment-click="drillProductCoverage"
            />
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <div class="chart-col">
              <div class="logbook-table-card">
                <div class="logbook-table-header">
                  <h3 class="subsection-heading">{{ `${$labels.project}s by ${$labels.product.toLowerCase()}` }}</h3>
                </div>
                <vue-good-table
                  :columns="sessionsByProductColumns"
                  :rows="projectsByProduct"
                  :pagination-options="{ enabled: projectsByProduct.length > 10, perPage: 10 }"
                  :search-options="{ enabled: false }"
                  :sort-options="{ enabled: true }"
                >
                  <template #table-row="props">
                    <span v-if="props.column.field === 'actions'">
                      <font-awesome-icon
                        icon="eye"
                        class="view-icon"
                        title="View sessions"
                        @click="drillProjectsForProduct(props.row)"
                      />
                    </span>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                  </template>
                </vue-good-table>
              </div>
            </div>
            </div>
            <div class="chart-row" style="margin-top: 20px;">
            <div class="chart-col">
              <div class="logbook-table-card">
                <div class="logbook-table-header">
                  <h3 class="subsection-heading">{{ `${$labels.product}s by pass rate` }}</h3>
                </div>
                <vue-good-table
                  :columns="productPassRateColumns"
                  :rows="productsByPassRate"
                  :pagination-options="{ enabled: productsByPassRate.length > 10, perPage: 10 }"
                  :search-options="{ enabled: false }"
                  :sort-options="{ enabled: true }"
                >
                  <template #table-row="props">
                    <span v-if="props.column.field === 'value'">{{ `${props.row.value}%` }}</span>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                  </template>
                </vue-good-table>
              </div>
            </div>
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <line-chart
              :title="`${$labels.product}s added over time`"
              :series="productsAddedTrendSeries"
              :buckets="productsAddedTrend.buckets"
              :note="productsAddedTrend.truncated ? `Showing the last ${productsAddedTrend.buckets.length} periods` : ''"
              :loading="loading.products"
              empty-text="No creation dates recorded yet."
              @point-click="drillProductsAddedBucket"
            >
              <template #actions>
                <granularity-toggle
                  v-model="productsAddedGranularity"
                  :options="granularityOptions"
                  aria-label="Products added trend granularity"
                />
              </template>
            </line-chart>
          </div>
        </section>
        </Transition>

        <!-- Projects -->
        <Transition name="tab-fade">
        <section v-show="activeTab === 'projects'" class="dashboard-section">
          <h2>{{ $labels.project }}s</h2>
          <div class="kpi-row">
            <stat-tile :label="`Total ${$labels.project}s`" :value="totalProjects" :loading="loading.projects" icon="sitemap" />
            <stat-tile label="Active" :value="activeProjectsCount" :loading="loading.projects" icon="circle-check" tone="good" />
            <stat-tile label="Locked" :value="lockedProjectsCount" :loading="loading.projects" icon="lock" tone="neutral" />
            <stat-tile :label="`${$labels.product}s in use`" :value="productsWithProjectsCount" :loading="loading.projects" icon="cubes" tone="neutral" />
            <stat-tile label="Behind pace" :value="behindPaceCount" :loading="loading.projects || entriesLoading" icon="triangle-exclamation" tone="critical" />
          </div>
          <div class="chart-row">
            <bar-chart
              :title="`${$labels.project}s by ${$labels.product.toLowerCase()}`"
              :items="projectsByProduct"
              :loading="loading.projects || loading.products"
              empty-text="No sessions recorded yet."
              @bar-click="drillProjectsForProduct"
            />
            <stacked-bar
              :title="`${$labels.project} status`"
              :segments="projectStatusSegments"
              :loading="loading.projects"
              empty-text="No sessions recorded yet."
              @segment-click="drillProjectStatus"
            />
          </div>

          <div class="logbook-table-card">
            <div class="logbook-table-header">
              <h3 class="subsection-heading">Trainee progress by session</h3>
              <div class="session-progress-filters">
                <select v-model="sessionProgressFilter" class="session-select">
                  <option value="">All sessions</option>
                  <option v-for="opt in sessionFilterOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
                <input
                  v-model="sessionProgressQuery"
                  type="text"
                  class="logbook-search-input"
                  placeholder="Search by trainee name"
                />
              </div>
            </div>

            <template v-if="sessionProgressFilter">
              <div v-if="sessionProgressChart" class="chart-toolbar">
                <granularity-toggle
                  v-model="sessionProgressGranularity"
                  :options="granularityOptions"
                  aria-label="Session progress chart granularity"
                />
              </div>
              <session-progress-chart
                v-if="sessionProgressChart"
                :title="`Cumulative progress — ${selectedSessionName}`"
                :series="sessionProgressChart.series"
                :buckets="sessionProgressChart.buckets"
                :note="sessionPassRateChart && sessionPassRateChart.passRate !== null
                  ? `Dashed line = expected pace toward the ${sessionPassRateChart.passRate}% pass rate. Bold lines = trainees behind pace, green = ahead or passed.`
                  : 'Dashed line = expected pace. Bold lines = trainees behind pace, green = ahead or passed.'"
                @point-click="drillSessionProgressPoint"
              />
              <p v-else class="viz-note" style="margin: 4px 0 16px;">
                Not enough data to chart this session yet — it needs valid start/end dates and at least one trainee with tasks assigned.
              </p>

              <bar-chart
                v-if="sessionPassRateChart"
                :title="`Progress vs. pass rate — ${selectedSessionName}`"
                :items="sessionPassRateChart.items"
                :max-items="sessionPassRateChart.items.length"
                :format-value="(v) => `${v}%`"
                :max-value="100"
                :legend="sessionPassRateChart.passRate !== null
                  ? [
                      { label: `At/above ${sessionPassRateChart.passRate}% pass rate`, tone: 'good' },
                      { label: `Below ${sessionPassRateChart.passRate}% pass rate`, tone: 'warning' },
                    ]
                  : []"
                empty-text="No trainees with tasks assigned yet."
                style="margin-top: 16px;"
                @bar-click="drillSessionPassRateBar"
              />
            </template>

            <p class="viz-note" style="margin: 16px 0 10px;">
              Sorted by furthest behind pace first. "Pace" compares task completion % against a target that rises to the practical element's pass rate (not 100%) as the session's timeline elapses - once a trainee clears the pass rate, they're shown as "Pass rate met" regardless of tasks left outstanding.
            </p>
            <vue-good-table
              :columns="sessionProgressColumns"
              :rows="filteredSessionProgress"
              :pagination-options="{ enabled: true, perPage: 10 }"
              :search-options="{ enabled: false }"
              :sort-options="{ enabled: true }"
            >
              <template #table-row="props">
                <span v-if="props.column.field === 'progressPct'">
                  {{ props.row.progressPct === null ? '—' : `${props.row.progressPct}% (${props.row.completed}/${props.row.total})` }}
                </span>
                <span v-else-if="props.column.field === 'paceStatus'">
                  <span class="status-chip" :class="paceStatusTone(props.row.paceStatus)">{{ props.row.paceStatus }}</span>
                </span>
                <span v-else-if="props.column.field === 'actions'">
                  <font-awesome-icon
                    icon="eye"
                    class="view-icon"
                    title="View sign-off timeline"
                    @click="openLogbookTimeline(props.row)"
                  />
                </span>
                <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
        </section>
        </Transition>

        <!-- Users -->
        <Transition name="tab-fade">
        <section v-show="activeTab === 'users'" class="dashboard-section">
          <h2>Users</h2>
          <div class="kpi-row">
            <stat-tile label="Total users" :value="totalUsers" :loading="loading.users" icon="users" />
            <stat-tile label="Active" :value="activeUsersCount" :loading="loading.users" icon="user-check" tone="good" />
            <stat-tile label="Inactive" :value="inactiveUsersCount" :loading="loading.users" icon="user-slash" tone="neutral" />
            <stat-tile label="Trainees" :value="traineeCount" :loading="loading.users" icon="graduation-cap" tone="neutral" />
            <stat-tile label="Assigned trainees" :value="assignedTraineeCount" :loading="loading.users || loading.logbooks" icon="book-open" tone="good" />
            <stat-tile label="Unassigned trainees" :value="unassignedTraineeCount" :loading="loading.users || loading.logbooks" icon="triangle-exclamation" :tone="unassignedTraineeCount > 0 ? 'warning' : 'good'" />
          </div>
          <div class="chart-row">
            <bar-chart
              title="Users by role"
              :items="roleCounts"
              :loading="loading.users"
              empty-text="No users recorded yet."
              @bar-click="drillUsersByRole"
            />
            <stacked-bar
              title="Account status"
              :segments="userStatusSegments"
              :loading="loading.users"
              empty-text="No users recorded yet."
              @segment-click="drillUserStatus"
            />
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <bar-chart
              v-if="ENABLE_USER_BIRTH_AND_CATEGORY"
              title="Trainees by license category"
              :items="traineesByCategory"
              :loading="loading.users"
              empty-text="No trainees recorded yet."
              @bar-click="drillTraineesByCategory"
            />
            <stacked-bar
              title="Trainee assignment"
              :segments="traineeAssignmentSegments"
              :loading="loading.users || loading.logbooks"
              empty-text="No trainees recorded yet."
              @segment-click="drillTraineeAssignment"
            />
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <bar-chart
              title="Busiest instructors"
              :items="instructorActivityLeaderboard"
              :loading="loading.users || loading.logbooks || entriesLoading || loading.projects || evaluationsLoading"
              empty-text="No instructor activity recorded yet."
              @bar-click="drillInstructorActivity"
            />
          </div>

          <div v-if="unassignedTrainees.length" class="logbook-table-card">
            <div class="logbook-table-header">
              <h3 class="subsection-heading">Unassigned trainees</h3>
              <span class="viz-note">Trainees with no logbook created yet</span>
            </div>
            <vue-good-table
              :columns="userDrilldownColumns"
              :rows="unassignedTraineeRows"
              :pagination-options="{ enabled: unassignedTraineeRows.length > 10, perPage: 10 }"
              :search-options="{ enabled: false }"
              :sort-options="{ enabled: true }"
            />
          </div>
        </section>
        </Transition>

        <!-- Tasks -->
        <Transition name="tab-fade">
        <section v-show="activeTab === 'tasks'" class="dashboard-section">
          <h2>Tasks</h2>
          <div class="kpi-row">
            <stat-tile label="Total tasks" :value="totalTasks" :loading="loading.tasks" icon="tasks" />
            <stat-tile label="Main tasks (MT)" :value="mtTaskCount" :loading="loading.tasks" icon="star" tone="good" />
            <stat-tile label="Additional tasks (AT)" :value="atTaskCount" :loading="loading.tasks" icon="plus" tone="neutral" />
            <stat-tile label="Chapters covered" :value="chaptersCoveredCount" :loading="loading.tasks" icon="book" tone="neutral" />
          </div>
          <div class="chart-row">
            <bar-chart
              title="Tasks by chapter"
              :items="topChaptersByTasks"
              :loading="loading.tasks"
              empty-text="No tasks recorded yet."
              @bar-click="drillTasksByChapter"
            />
            <stacked-bar
              title="Task type breakdown"
              :segments="taskTypeSegments"
              :loading="loading.tasks"
              empty-text="No tasks recorded yet."
              @segment-click="drillTaskType"
            />
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <stacked-bar
              title="Tasks by category"
              :segments="taskCategorySegments"
              :loading="loading.tasks"
              empty-text="No tasks recorded yet."
              @segment-click="drillTaskCategory"
            />
            <stacked-bar
              title="Tasks by required skill"
              :segments="taskSkillSegments"
              :loading="loading.tasks"
              empty-text="No tasks recorded yet."
              @segment-click="drillTaskSkill"
            />
          </div>
          <div class="chart-row" style="margin-top: 20px;">
            <stacked-bar
              title="Task catalog utilization"
              :segments="taskUtilizationSegments"
              :loading="loading.tasks || loading.logbooks || entriesLoading"
              empty-text="No tasks recorded yet."
              @segment-click="drillTaskUtilization"
            />
            <stacked-bar
              title="Task scope utilization"
              :segments="taskScopeSegments"
              :loading="loading.tasks || loading.logbooks || entriesLoading"
              empty-text="No tasks recorded yet."
              @segment-click="drillTaskScope"
            />
          </div>
        </section>
        </Transition>

        <!-- Logbook Entries -->
        <Transition name="tab-fade">
        <section v-show="activeTab === 'logbooks'" class="dashboard-section">
          <h2>Logbook Entries</h2>
          <div class="kpi-row">
            <stat-tile label="Total logbooks" :value="totalLogbooks" :loading="loading.logbooks" icon="book-open" />
            <stat-tile label="Total entries" :value="totalEntries" :loading="loading.logbooks || entriesLoading" icon="list" tone="neutral" />
            <stat-tile label="Completed entries" :value="completedEntriesCount" :loading="loading.logbooks || entriesLoading" icon="circle-check" tone="good" />
            <stat-tile label="Revoked entries" :value="revokedEntriesCount" :loading="loading.logbooks || entriesLoading" icon="ban" tone="critical" />
            <stat-tile label="Avg. sign-off turnaround" :value="avgSignOffTurnaroundDisplay" :loading="loading.logbooks || entriesLoading" icon="stopwatch" tone="neutral" />
            <stat-tile label="At-risk trainees" :value="atRiskLogbooks.length" :loading="loading.logbooks || entriesLoading" icon="triangle-exclamation" tone="critical" />
          </div>
          <div v-if="entriesError" class="load-error-banner">
            <font-awesome-icon icon="triangle-exclamation" /> {{ entriesError }}
          </div>
          <div class="chart-row">
            <bar-chart
              title="Logbooks with the most entries"
              :items="topLogbooksByEntries"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No logbook entries recorded yet."
              @bar-click="drillLogbookFromChart"
            />
            <stacked-bar
              title="Entry completion"
              :segments="entryCompletionSegments"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No logbook entries recorded yet."
              @segment-click="drillEntryCompletion"
            />
          </div>
          <div class="chart-row">
            <div class="chart-col">
              <div class="chart-toolbar">
                <granularity-toggle
                  v-model="enabledGranularity"
                  :options="granularityOptions"
                  aria-label="Task assignment trend granularity"
                />
              </div>
              <line-chart
                title="Task assignments over time"
                :series="enabledTrendSeries"
                :buckets="enabledTrend.buckets"
                :note="enabledTrend.truncated ? `Showing the last ${enabledTrend.buckets.length} periods` : ''"
                :loading="loading.logbooks || entriesLoading"
                empty-text="No tasks have been assigned yet."
                @point-click="drillEnabledBucket"
              />
            </div>
            <bar-chart
              title="Instructors by tasks assigned"
              :items="instructorEnableLeaderboard"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No instructor task-enabling recorded yet."
              @bar-click="drillInstructorEnables"
            />
          </div>
          <div class="chart-row">
            <bar-chart
              title="Least assigned tasks"
              :items="leastEnabledTasks"
              :loading="loading.logbooks || entriesLoading || loading.tasks"
              empty-text="No tasks recorded yet."
              :max-value="maxEnabledCount"
              @bar-click="drillTaskEnableCount"
            />
            <bar-chart
              title="Most assigned tasks"
              :items="mostEnabledTasks"
              :loading="loading.logbooks || entriesLoading || loading.tasks"
              empty-text="No tasks have been assigned yet."
              :max-value="maxEnabledCount"
              color="#0ca30c"
              @bar-click="drillTaskEnableCount"
            />
          </div>
          <div class="chart-row">
            <stacked-bar
              title="Completed tasks by training location"
              :segments="entryLocationSegments"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No completed tasks recorded yet."
              @segment-click="drillEntryLocation"
            />
          </div>
          <div class="chart-row">
            <div class="chart-col">
              <div class="chart-toolbar">
                <granularity-toggle
                  v-model="signOffGranularity"
                  :options="granularityOptions"
                  aria-label="Sign-off trend granularity"
                />
              </div>
              <line-chart
                title="Sign-offs over time"
                :series="signOffTrendSeries"
                :buckets="signOffTrend.buckets"
                :note="signOffTrend.truncated ? `Showing the last ${signOffTrend.buckets.length} periods` : ''"
                :loading="loading.logbooks || entriesLoading"
                empty-text="No sign-off activity recorded yet."
                @point-click="drillSignOffBucket"
              />
            </div>
            <bar-chart
              title="Instructors by sign-offs"
              :items="instructorSignOffLeaderboard"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No instructor sign-offs recorded yet."
              @bar-click="drillInstructorEntries"
            />
          </div>
          <div class="chart-row">
            <bar-chart
              title="Sign-off turnaround"
              :items="turnaroundBuckets"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No sign-off activity recorded yet."
              @bar-click="drillTurnaroundBucket"
            />
            <bar-chart
              title="Revocations by instructor"
              :items="instructorRevocationCounts"
              :loading="loading.logbooks || entriesLoading"
              empty-text="No revoked entries recorded yet."
              @bar-click="drillInstructorRevocations"
            />
          </div>
          <div class="chart-row">
            <bar-chart
              title="Lowest completion rate by task"
              :items="lowestCompletionTasks"
              :loading="loading.logbooks || entriesLoading"
              empty-text="Not enough data yet to surface bottleneck tasks."
              :format-value="(v) => `${v}%`"
              :max-value="100"
              @bar-click="drillBottleneckTask"
            />
            <bar-chart
              title="Highest completion rate by task"
              :items="highestCompletionTasks"
              :loading="loading.logbooks || entriesLoading"
              empty-text="Not enough data yet to surface task completion."
              :format-value="(v) => `${v}%`"
              :max-value="100"
              color="#0ca30c"
              @bar-click="drillTopCompletionTask"
            />
          </div>
          <div v-if="atRiskLogbooks.length" class="logbook-table-card">
            <div class="logbook-table-header">
              <h3 class="subsection-heading">At-risk trainees</h3>
              <span class="viz-note">{{ atRiskNote }}</span>
            </div>
            <vue-good-table
              :columns="atRiskColumns"
              :rows="atRiskLogbooks"
              :pagination-options="{ enabled: atRiskLogbooks.length > 10, perPage: 10 }"
              :search-options="{ enabled: false }"
              :sort-options="{ enabled: true }"
            >
              <template #table-row="props">
                <span v-if="props.column.field === 'daysUntilEnd'" :class="{ 'text-critical': props.row.daysUntilEnd < 0 }">
                  {{ props.row.daysUntilEnd < 0 ? `${Math.abs(props.row.daysUntilEnd)} days overdue` : `${props.row.daysUntilEnd} days` }}
                </span>
                <span v-else>{{ props.formattedRow[props.column.field] }}</span>
              </template>
            </vue-good-table>
          </div>

          <div class="logbook-table-card">
            <div class="logbook-table-header">
              <h3 class="subsection-heading">Browse logbooks</h3>
              <input
                v-model="logbookSearchQuery"
                type="text"
                class="logbook-search-input"
                placeholder="Search by trainee or logbook name"
              />
            </div>
            <vue-good-table
              :columns="logbookColumns"
              :rows="filteredLogbookSummary"
              :pagination-options="{ enabled: true, perPage: 10 }"
              :search-options="{ enabled: false }"
              :sort-options="{ enabled: true }"
            >
              <template #table-row="props">
                <span v-if="props.column.field === 'status'">
                  <span class="status-chip" :class="props.row.status === 'Active' ? 'tone-good' : 'tone-neutral'">
                    {{ props.row.status }}
                  </span>
                </span>
                <span v-else-if="props.column.field === 'classroomPct' || props.column.field === 'aircraftPct'">
                  {{ props.row[props.column.field] === null ? '—' : `${props.row[props.column.field]}%` }}
                </span>
                <span v-else-if="props.column.field === 'actions'">
                  <font-awesome-icon
                    icon="eye"
                    class="view-icon"
                    title="View sign-off timeline"
                    @click="openLogbookTimeline(props.row)"
                  />
                </span>
                <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
        </section>
        </Transition>

        <!-- Evaluations -->
        <Transition name="tab-fade">
        <section v-show="activeTab === 'evaluations'" class="dashboard-section">
          <h2>Evaluations</h2>
          <div class="kpi-row">
            <stat-tile label="Total evaluations" :value="totalEvaluations" :loading="loading.projects || evaluationsLoading" icon="clipboard-check" />
            <stat-tile label="Passed" :value="passedEvaluationsCount" :loading="loading.projects || evaluationsLoading" icon="circle-check" tone="good" />
            <stat-tile label="Remedial required" :value="remedialRequiredCount" :loading="loading.projects || evaluationsLoading" icon="triangle-exclamation" tone="warning" />
            <stat-tile label="Failed" :value="failedEvaluationsCount" :loading="loading.projects || evaluationsLoading" icon="ban" tone="critical" />
            <stat-tile label="Pending" :value="pendingEvaluationsCount" :loading="loading.projects || evaluationsLoading" icon="hourglass-half" tone="neutral" />
          </div>
          <div v-if="evaluationsError" class="load-error-banner">
            <font-awesome-icon icon="triangle-exclamation" /> {{ evaluationsError }}
          </div>
          <div class="chart-row">
            <stacked-bar
              title="Evaluation outcomes"
              :segments="evaluationStatusSegments"
              :loading="loading.projects || evaluationsLoading"
              empty-text="No evaluations recorded yet."
              @segment-click="drillEvaluationStatus"
            />
            <bar-chart
              title="Evaluations by instructor"
              :items="evaluationsByInstructor"
              :loading="loading.projects || evaluationsLoading"
              empty-text="No evaluations recorded yet."
              @bar-click="drillEvaluationsByInstructor"
            />
          </div>
          <div class="chart-row">
            <line-chart
              title="Evaluations over time"
              :series="evaluationsTrendSeries"
              :buckets="evaluationsTrend.buckets"
              :note="evaluationsTrend.truncated ? `Showing the last ${evaluationsTrend.buckets.length} periods` : ''"
              :loading="loading.projects || evaluationsLoading"
              empty-text="No evaluations recorded yet."
              @point-click="drillEvaluationsTrend"
            >
              <template #actions>
                <granularity-toggle
                  v-model="evaluationsGranularity"
                  :options="granularityOptions"
                  aria-label="Evaluations trend granularity"
                />
              </template>
            </line-chart>
            <bar-chart
              title="Criteria most often marked unsatisfactory"
              :items="criteriaFailureRates"
              :loading="loading.projects || evaluationsLoading"
              empty-text="Not enough data yet to surface criteria trends."
              :format-value="(v) => `${v}%`"
              :max-value="100"
              @bar-click="drillCriterionFailures"
            />
          </div>
          <div class="chart-row">
            <stacked-bar
              :title="evaluationSessionFilter ? `Evaluation outcomes — ${selectedEvaluationSessionName}` : `Evaluation outcomes by ${$labels.project.toLowerCase()}`"
              :segments="evaluationSessionOutcomeSegments"
              :loading="loading.projects || evaluationsLoading"
              :empty-text="evaluationSessionFilter ? 'No evaluations recorded for this session yet.' : `Select a ${$labels.project.toLowerCase()} to see its evaluation outcome breakdown.`"
              @segment-click="drillEvaluationSessionStatus"
            >
              <template #actions>
                <select v-model="evaluationSessionFilter" class="session-select">
                  <option value="">Select a {{ $labels.project.toLowerCase() }}…</option>
                  <option v-for="opt in sessionFilterOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </template>
            </stacked-bar>
          </div>

          <div v-if="evaluationsNeedingAttention.length" class="logbook-table-card">
            <div class="logbook-table-header">
              <h3 class="subsection-heading">Trainees needing attention</h3>
              <span class="viz-note">Failed, remedial required, or awaiting signature — sorted worst first</span>
            </div>
            <vue-good-table
              :columns="evaluationAttentionColumns"
              :rows="evaluationsNeedingAttention"
              :pagination-options="{ enabled: evaluationsNeedingAttention.length > 10, perPage: 10 }"
              :search-options="{ enabled: false }"
              :sort-options="{ enabled: true }"
            >
              <template #table-row="props">
                <span v-if="props.column.field === 'status'">
                  <span class="status-chip" :class="'tone-' + evaluationTone(props.row.status)">{{ props.row.status }}</span>
                </span>
                <span v-else-if="props.column.field === 'actions'">
                  <font-awesome-icon
                    icon="eye"
                    class="view-icon"
                    title="View sign-off timeline"
                    @click="openEvaluationLogbookTimeline(props.row)"
                  />
                </span>
                <span v-else :title="String(props.formattedRow[props.column.field] ?? '')">
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
        </section>
        </Transition>

      </main>
    </div>

    <logbook-sign-off-modal
      v-if="selectedLogbookForTimeline"
      :logbook="selectedLogbookForTimeline"
      :events="selectedLogbookEvents"
      :segments="selectedLogbookSegments"
      :skipped-count="selectedLogbookSkippedCount"
      @close="closeLogbookTimeline"
    />

    <product-details-modal
      v-if="showProductDetails"
      :tasks="tasks"
      @close="closeProductDetails"
    />

    <drilldown-panel
      v-if="drilldown"
      :title="drilldown.title"
      :rows="drilldown.rows"
      :columns="drilldown.columns"
      @clear="clearDrilldown"
    />
  </div>
</template>

<script>
import PageHeader from './Header.vue';
import { ENABLE_USER_BIRTH_AND_CATEGORY } from '@/config/featureFlags';
import axios from 'axios';
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css';
import StatTile from './dashboard/StatTile.vue';
import BarChart from './dashboard/BarChart.vue';
import StackedBar from './dashboard/StackedBar.vue';
import LineChart from './dashboard/LineChart.vue';
import GranularityToggle from './dashboard/GranularityToggle.vue';
import LogbookSignOffModal from './dashboard/LogbookSignOffModal.vue';
import ProductDetailsModal from './products/ProductDetailsModal.vue';
import DrilldownPanel from './dashboard/DrilldownPanel.vue';
import SessionProgressChart from './dashboard/SessionProgressChart.vue';
import InsightCard from './dashboard/InsightCard.vue';
import RecentActivityFeed from './dashboard/RecentActivityFeed.vue';
import { parseSignOffDate, buildTrend, buildCumulativeTrend, GRANULARITY_OPTIONS } from './dashboard/signOffTrend.js';

const ROLE_LABELS = {
  admin: 'Administrator',
  examiner: 'Examiner',
  practical_manager: 'Practical Element Manager',
  project_manager: 'Session Manager',
  instructor: 'Instructor',
  trainee: 'Trainee',
  assessor: 'Assessor',
};

// Sign-off turnaround histogram buckets (days between trainee completion and
// instructor sign-off). `max` is exclusive; the last bucket has no upper bound.
const TURNAROUND_BUCKETS = [
  { label: '< 1 day', min: 0, max: 1 },
  { label: '1-3 days', min: 1, max: 3 },
  { label: '3-7 days', min: 3, max: 7 },
  { label: '1-2 weeks', min: 7, max: 14 },
  { label: '2+ weeks', min: 14, max: Infinity },
];

// A task needs at least this many logbook rows before its completion % is
// shown as a "bottleneck" - otherwise a task assigned to 1-2 trainees looks
// like a crisis purely from small-sample noise.
const MIN_TASK_SAMPLE = 3;

// A logbook only shows up as "at risk" once its session is within this many
// days of ending (or has already ended)...
const RISK_WINDOW_DAYS = 10;
// ...and it's had no completion/sign-off activity in at least this many days.
const STALE_ACTIVITY_DAYS = 14;

// How far a trainee's actual task completion % can trail the % of the
// session's timeline that has already elapsed before they're flagged as
// "Behind pace" - e.g. 60% of the session elapsed but only 35% of tasks
// done (a 25pt gap) trips this at the default 20.
const PACE_BEHIND_THRESHOLD = 20;

const EVALUATION_CRITERIA = [
  'Consults the correct documentation',
  'Scans the environment before starting and during the task to ensure safety',
  'Reads/interprets the safety warning correctly',
  'Informs/communicates with appropriate people correctly',
  'Interprets the reports, indications and documentation correctly',
  'Follows the procedure steps',
  'Reacts properly to changes during task performance to ensure safety',
  'Takes into account interactions between systems',
  'Restores the aircraft back to initial condition (or appropriate condition depending on the circumstances)',
];

// A criterion needs at least this many recorded ratings before its failure rate is used
const MIN_CRITERION_SAMPLE = 3;

export default {
  name: 'examinerDashboard',
  components: {
    PageHeader,
    StatTile,
    BarChart,
    StackedBar,
    LineChart,
    SessionProgressChart,
    InsightCard,
    RecentActivityFeed,
    GranularityToggle,
    LogbookSignOffModal,
    ProductDetailsModal,
    DrilldownPanel,
    VueGoodTable,
  },
  data() {
    return {
      activeTab: 'overview',
      drilldown: null,
      showProductDetails: false,
      products: [],
      projects: [],
      users: [],
      tasks: [],
      logbooks: [],
      logbookEntries: [],
      loading: {
        products: false,
        projects: false,
        users: false,
        tasks: false,
        logbooks: false,
      },
      loadErrors: [],
      entriesLoaded: false,
      entriesLoading: false,
      entriesError: null,
      evaluations: [],
      evaluationsLoaded: false,
      evaluationsLoading: false,
      evaluationsError: null,
      logbookSearchQuery: '',
      sessionProgressQuery: '',
      sessionProgressFilter: '',
      sessionProgressGranularity: 'week',
      evaluationsGranularity: 'month',
      evaluationSessionFilter: '',
      selectedLogbookForTimeline: null,
      signOffGranularity: 'month',
      enabledGranularity: 'month',
      productsAddedGranularity: 'month',
      granularityOptions: GRANULARITY_OPTIONS,
    };
  },
  computed: {
    ENABLE_USER_BIRTH_AND_CATEGORY() {
      return ENABLE_USER_BIRTH_AND_CATEGORY;
    },
    tabs() {
      return [
        { key: 'overview', label: 'Overview', icon: 'chart-pie' },
        { key: 'products', label: `${this.$labels.product}s`, icon: 'cubes' },
        { key: 'projects', label: `${this.$labels.project}s`, icon: 'sitemap' },
        { key: 'users', label: 'Users', icon: 'users' },
        { key: 'tasks', label: 'Tasks', icon: 'tasks' },
        { key: 'logbooks', label: 'Logbook Entries', icon: 'book-open' },
        { key: 'evaluations', label: 'Evaluations', icon: 'clipboard-check' },
      ];
    },

    // ---------- Drill-down column presets ----------
    taskDrilldownColumns() {
      return [
        { label: 'Chapter', field: 'chapter', sortable: true },
        { label: 'Task No', field: 'task_no', sortable: true },
        { label: 'MT/AT', field: 'type', sortable: true },
        { label: 'Description', field: 'description', sortable: true },
      ];
    },
    userDrilldownColumns() {
      return [
        { label: 'Name', field: 'name', sortable: true },
        { label: 'Email', field: 'email', sortable: true },
        { label: 'Roles', field: 'roles', sortable: true },
        { label: 'Status', field: 'status', sortable: true },
      ];
    },
    projectDrilldownColumns() {
      return [
        { label: `${this.$labels.project} Name`, field: 'name', sortable: true },
        { label: 'Status', field: 'status', sortable: true },
      ];
    },
    productDrilldownColumns() {
      return [
        { label: `${this.$labels.product} Name`, field: 'name', sortable: true },
        { label: 'Tasks', field: 'taskCount', sortable: true },
      ];
    },
    sessionsByProductColumns() {
      return [
        { label: `${this.$labels.product} Name`, field: 'label', sortable: true },
        { label: `${this.$labels.project}s`, field: 'value', sortable: true, thClass: 'vgt-foreign-id-column', tdClass: 'vgt-id-column' },
        { label: 'Actions', field: 'actions', sortable: false, thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
      ];
    },
    productPassRateColumns() {
      return [
        { label: `${this.$labels.product} Name`, field: 'label', sortable: true },
        { label: 'Pass rate', field: 'value', sortable: true, thClass: 'vgt-foreign-id-column', tdClass: 'vgt-id-column' },
      ];
    },
    entryDrilldownColumns() {
      return [
        { label: 'Trainee / Logbook', field: 'logbookLabel', sortable: true },
        { label: 'Task', field: 'taskLabel', sortable: true },
        { label: 'Instructor', field: 'instructorLabel', sortable: true },
        { label: 'Date', field: 'dateLabel', sortable: true },
      ];
    },

    // ---------- Products ----------
    totalProducts() {
      return this.products.length;
    },
    taskCountByProduct() {
      const map = new Map();
      this.tasks.forEach((t) => {
        map.set(t.product_id, (map.get(t.product_id) || 0) + 1);
      });
      return map;
    },
    productsWithTasksCount() {
      return this.products.filter((p) => (this.taskCountByProduct.get(p.product_id) || 0) > 0).length;
    },
    productsWithoutTasksCount() {
      return this.totalProducts - this.productsWithTasksCount;
    },
    topProductsByTasks() {
      return this.products
        .map((p) => ({ label: p.name, value: this.taskCountByProduct.get(p.product_id) || 0, product_id: p.product_id }))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
    },
    productCoverageSegments() {
      return [
        { label: 'With tasks', value: this.productsWithTasksCount, tone: 'accent', id: 'with' },
        { label: 'Without tasks', value: this.productsWithoutTasksCount, tone: 'warning', id: 'without' },
      ];
    },
    avgProductPassRate() {
      const rates = this.products
        .map((p) => (p.pass_rate !== null && p.pass_rate !== undefined && p.pass_rate !== '' ? Number(p.pass_rate) : null))
        .filter((r) => r !== null && !Number.isNaN(r));
      if (!rates.length) return null;
      return Math.round(rates.reduce((sum, r) => sum + r, 0) / rates.length);
    },
    avgProductPassRateDisplay() {
      return this.avgProductPassRate !== null ? `${this.avgProductPassRate}%` : '—';
    },
    productsByPassRate() {
      return this.products
        .filter((p) => p.pass_rate !== null && p.pass_rate !== undefined && p.pass_rate !== '')
        .map((p) => ({ label: p.name, value: Number(p.pass_rate), product_id: p.product_id }))
        .sort((a, b) => b.value - a.value);
    },
    productsAddedTrendSeries() {
      return [{ key: 'added', label: `${this.$labels.product}s added`, colorVar: '--series-1' }];
    },
    productsAddedRecords() {
      const records = [];
      this.products.forEach((p) => {
        const date = parseSignOffDate(p.created_at);
        if (date) records.push({ date, seriesKey: 'added' });
      });
      return records;
    },
    productsAddedTrend() {
      return buildTrend(this.productsAddedRecords, this.productsAddedGranularity);
    },

    // ---------- Projects ----------
    totalProjects() {
      return this.projects.length;
    },
    activeProjectsCount() {
      return this.projects.filter((p) => !p.locked).length;
    },
    lockedProjectsCount() {
      return this.totalProjects - this.activeProjectsCount;
    },
    productsWithProjectsCount() {
      return new Set(this.projects.map((p) => p.product_id)).size;
    },
    projectsByProduct() {
      const productNameById = new Map(this.products.map((p) => [p.product_id, p.name]));
      const counts = new Map();
      this.projects.forEach((proj) => {
        counts.set(proj.product_id, (counts.get(proj.product_id) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([productId, value]) => ({
          label: productNameById.get(productId) || `${this.$labels.product} ${productId}`,
          value,
          product_id: productId,
        }))
        .sort((a, b) => b.value - a.value);
    },
    projectStatusSegments() {
      return [
        { label: 'Active', value: this.activeProjectsCount, tone: 'accent', id: 'active' },
        { label: 'Locked', value: this.lockedProjectsCount, tone: 'neutral', id: 'locked' },
      ];
    },

    // ---------- Users ----------
    totalUsers() {
      return this.users.length;
    },
    activeUsersCount() {
      return this.users.filter((u) => !!u.is_active).length;
    },
    inactiveUsersCount() {
      return this.totalUsers - this.activeUsersCount;
    },
    roleCounts() {
      const counts = new Map();
      this.users.forEach((u) => {
        (Array.isArray(u.roles) ? u.roles : []).forEach((role) => {
          counts.set(role, (counts.get(role) || 0) + 1);
        });
      });
      return Array.from(counts.entries())
        .map(([role, value]) => ({ label: ROLE_LABELS[role] || role, value, role }))
        .sort((a, b) => b.value - a.value);
    },
    traineeCount() {
      const match = this.roleCounts.find((r) => r.label === ROLE_LABELS.trainee);
      return match ? match.value : 0;
    },
    userStatusSegments() {
      return [
        { label: 'Active', value: this.activeUsersCount, tone: 'accent', id: 'active' },
        { label: 'Inactive', value: this.inactiveUsersCount, tone: 'neutral', id: 'inactive' },
      ];
    },
    traineeUsers() {
      return this.users.filter((u) => Array.isArray(u.roles) && u.roles.includes('trainee'));
    },
    traineeIdsWithLogbooks() {
      return new Set(this.logbooks.map((lb) => lb.trainee_id));
    },
    assignedTraineeCount() {
      return this.traineeUsers.filter((u) => this.traineeIdsWithLogbooks.has(u.user_id)).length;
    },
    unassignedTraineeCount() {
      return this.traineeUsers.length - this.assignedTraineeCount;
    },
    unassignedTrainees() {
      return this.traineeUsers.filter((u) => !this.traineeIdsWithLogbooks.has(u.user_id));
    },
    unassignedTraineeRows() {
      return this.unassignedTrainees.map((u) => this.userDrilldownRow(u));
    },
    traineesByCategory() {
      const counts = new Map();
      this.traineeUsers.forEach((u) => {
        const cat = u.category && u.category.trim() ? u.category.trim() : 'Unspecified';
        counts.set(cat, (counts.get(cat) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([category, value]) => ({ label: category, value, category }))
        .sort((a, b) => b.value - a.value);
    },
    traineeAssignmentSegments() {
      return [
        { label: 'Assigned', value: this.assignedTraineeCount, tone: 'good', id: 'assigned' },
        { label: 'Unassigned', value: this.unassignedTraineeCount, tone: 'warning', id: 'unassigned' },
      ];
    },
    instructorActivityColumns() {
      return [
        { label: 'Activity', field: 'activity', sortable: false },
        { label: 'Count', field: 'count', sortable: true },
      ];
    },
    // Ranks instructors by total activity
    instructorActivityLeaderboard() {
      const counts = new Map();
      const bump = (id, key) => {
        if (!id) return;
        if (!counts.has(id)) counts.set(id, { signOffs: 0, enables: 0, evaluations: 0 });
        counts.get(id)[key] += 1;
      };
      this.logbookEntries.forEach((e) => {
        if (e.instructor_id && parseSignOffDate(e.ins_so)) bump(e.instructor_id, 'signOffs');
        if (e.enabled && e.ins_enabled_id) bump(e.ins_enabled_id, 'enables');
      });
      this.evaluations.forEach((e) => {
        if (e.instructor_id) bump(e.instructor_id, 'evaluations');
      });
      return Array.from(counts.entries())
        .map(([instructorId, breakdown]) => ({
          label: this.instructorLabelFor(instructorId),
          value: breakdown.signOffs + breakdown.enables + breakdown.evaluations,
          instructor_id: instructorId,
          breakdown,
        }))
        .sort((a, b) => b.value - a.value);
    },

    // ---------- Tasks ----------
    totalTasks() {
      return this.tasks.length;
    },
    mtTaskCount() {
      return this.tasks.filter((t) => t.manob === 'MT').length;
    },
    atTaskCount() {
      return this.tasks.filter((t) => t.manob === 'AT').length;
    },
    chaptersCoveredCount() {
      return new Set(this.tasks.map((t) => t.chapt_no).filter((c) => c !== undefined && c !== null && c !== '')).size;
    },
    topChaptersByTasks() {
      const counts = new Map();
      this.tasks.forEach((t) => {
        const chapter = t.chapt_no || 'Unspecified';
        counts.set(chapter, (counts.get(chapter) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([chapter, value]) => ({ label: `Chapter ${chapter}`, value, chapter }))
        .sort((a, b) => b.value - a.value);
    },
    taskTypeSegments() {
      const other = this.totalTasks - this.mtTaskCount - this.atTaskCount;
      const segments = [
        { label: 'Main Task (MT)', value: this.mtTaskCount, tone: 'accent', id: 'mt' },
        { label: 'Additional Task (AT)', value: this.atTaskCount, tone: 'neutral', id: 'at' },
      ];
      if (other > 0) segments.push({ label: 'Unspecified', value: other, tone: 'neutral', id: 'unspecified' });
      return segments;
    },
    taskCategorySegments() {
      const counts = { AF: 0, AV: 0, PP: 0 };
      let other = 0;
      this.tasks.forEach((t) => {
        if (t.category === 'AF' || t.category === 'AV' || t.category === 'PP') counts[t.category] += 1;
        else other += 1;
      });
      const segments = [
        { label: 'AF', value: counts.AF, tone: 'accent', id: 'AF' },
        { label: 'AV', value: counts.AV, tone: 'neutral', id: 'AV' },
        { label: 'PP', value: counts.PP, tone: 'good', id: 'PP' },
      ];
      if (other > 0) segments.push({ label: 'Unspecified', value: other, tone: 'warning', id: 'unspecified' });
      return segments;
    },
    taskSkillSegments() {
      const counts = { B1: 0, B2: 0 };
      let combined = 0;
      let other = 0;
      this.tasks.forEach((t) => {
        if (t.skill === 'B1') counts.B1 += 1;
        else if (t.skill === 'B2') counts.B2 += 1;
        else if (t.skill === 'B1/B2') combined += 1;
        else other += 1;
      });
      const segments = [
        { label: 'B1', value: counts.B1, tone: 'accent', id: 'B1' },
        { label: 'B2', value: counts.B2, tone: 'neutral', id: 'B2' },
        { label: 'B1/B2', value: combined, tone: 'good', id: 'B1/B2' },
      ];
      if (other > 0) segments.push({ label: 'Unspecified', value: other, tone: 'warning', id: 'unspecified' });
      return segments;
    },
    // Reuses taskEnableCounts (built for the Logbooks tab's most/least
    // enabled charts) rather than recomputing enable-counts from scratch.
    taskUtilizationSegments() {
      const everEnabled = this.taskEnableCounts.filter((t) => t.value > 0).length;
      const neverEnabled = this.taskEnableCounts.length - everEnabled;
      return [
        { label: 'Assigned', value: everEnabled, tone: 'good', id: 'ever' },
        { label: 'Never assigned', value: neverEnabled, tone: 'warning', id: 'never' },
      ];
    },
    taskScopeCounts() {
      const counts = new Map();
      let unspecified = 0;
      this.tasks.forEach((t) => {
        const tokens = (t.scope || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (!tokens.length) {
          unspecified += 1;
          return;
        }
        tokens.forEach((token) => counts.set(token, (counts.get(token) || 0) + 1));
      });
      return { counts, unspecified };
    },
    taskScopeSegments() {
      const { counts, unspecified } = this.taskScopeCounts;
      const segments = Array.from(counts.entries())
        .map(([scope, value]) => ({ label: scope, value, id: scope }))
        .sort((a, b) => b.value - a.value);
      if (unspecified > 0) segments.push({ label: 'Unspecified', value: unspecified, tone: 'warning', id: 'unspecified' });
      return segments;
    },

    // ---------- Logbook entries ----------
    totalLogbooks() {
      return this.logbooks.length;
    },
    totalEntries() {
      return this.logbookEntries.length;
    },
    completedEntriesCount() {
      return this.logbookEntries.filter((e) => e.completed && !e.is_revoked).length;
    },
    revokedEntriesCount() {
      return this.logbookEntries.filter((e) => e.is_revoked).length;
    },
    pendingEntriesCount() {
      return this.totalEntries - this.completedEntriesCount - this.revokedEntriesCount;
    },
    entryCompletionSegments() {
      return [
        { label: 'Completed', value: this.completedEntriesCount, tone: 'good', id: 'completed' },
        { label: 'Pending', value: this.pendingEntriesCount, tone: 'warning', id: 'pending' },
        { label: 'Revoked', value: this.revokedEntriesCount, tone: 'critical', id: 'revoked' },
      ];
    },
    topLogbooksByEntries() {
      const logbookById = new Map(this.logbooks.map((lb) => [lb.logbook_id, lb]));
      const counts = new Map();
      this.logbookEntries.forEach((e) => {
        counts.set(e.logbook_id, (counts.get(e.logbook_id) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([logbookId, value]) => {
          const lb = logbookById.get(logbookId);
          const label = lb ? (lb.trainee_full_name || lb.name || `Logbook ${logbookId}`) : `Logbook ${logbookId}`;
          return { label, value, logbook_id: logbookId };
        })
        .sort((a, b) => b.value - a.value);
    },

    // ---------- Sign-offs over time ----------
    signOffTrendSeries() {
      return [
        { key: 'completed', label: 'Trainee completions', colorVar: '--series-1' },
        { key: 'signedOff', label: 'Instructor sign-offs', colorVar: '--series-2' },
      ];
    },
    signOffRecords() {
      const records = [];
      this.logbookEntries.forEach((e) => {
        const completedDate = parseSignOffDate(e.completed);
        if (completedDate) records.push({ date: completedDate, seriesKey: 'completed' });
        const signOffDate = parseSignOffDate(e.ins_so);
        if (signOffDate) records.push({ date: signOffDate, seriesKey: 'signedOff' });
      });
      return records;
    },
    signOffTrend() {
      return buildTrend(this.signOffRecords, this.signOffGranularity);
    },
    recentActivityTrend() {
      const trend = buildTrend(this.signOffRecords, 'day');
      return { buckets: trend.buckets.slice(-30) };
    },
    // Split by trainee completions and instructor sign offs
    instructorSignOffLeaderboard() {
      const counts = new Map();
      this.logbookEntries.forEach((e) => {
        if (!e.instructor_id || !parseSignOffDate(e.ins_so)) return;
        counts.set(e.instructor_id, (counts.get(e.instructor_id) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([instructorId, value]) => {
          const user = this.users.find((u) => u.user_id === instructorId);
          const label = user ? `${user.first_name} ${user.last_name}` : `Instructor ${instructorId}`;
          return { label, value, instructor_id: instructorId };
        })
        .sort((a, b) => b.value - a.value);
    },

    // ---------- Task enablement ----------
    enabledTrendSeries() {
      return [{ key: 'enabled', label: 'Tasks assigned', colorVar: '--series-1' }];
    },
    enabledTrend() {
      const records = [];
      this.logbookEntries.forEach((e) => {
        if (!e.enabled) return;
        const date = parseSignOffDate(e.selected_date);
        if (date) records.push({ date, seriesKey: 'enabled' });
      });
      return buildTrend(records, this.enabledGranularity);
    },
    instructorEnableLeaderboard() {
      const counts = new Map();
      this.logbookEntries.forEach((e) => {
        if (!e.enabled || !e.ins_enabled_id) return;
        counts.set(e.ins_enabled_id, (counts.get(e.ins_enabled_id) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([instructorId, value]) => ({ label: this.instructorLabelFor(instructorId), value, instructor_id: instructorId }))
        .sort((a, b) => b.value - a.value);
    },

    // ---------- Task enablement (doesn't apply for int only) ----------
    taskEnableCounts() {
      const counts = new Map();
      this.logbookEntries.forEach((e) => {
        if (!e.enabled) return;
        counts.set(e.task_id, (counts.get(e.task_id) || 0) + 1);
      });
      return this.tasks.map((t) => ({ label: this.taskLabelFor(t.task_id), value: counts.get(t.task_id) || 0, task_id: t.task_id }));
    },
    mostEnabledTasks() {
      return [...this.taskEnableCounts].sort((a, b) => b.value - a.value).slice(0, 10);
    },
    // Sorted ascending
    leastEnabledTasks() {
      return [...this.taskEnableCounts].sort((a, b) => a.value - b.value).slice(0, 10);
    },
    // Shared by both charts above so their bars sit on the same scale
    maxEnabledCount() {
      return Math.max(1, ...this.taskEnableCounts.map((t) => t.value));
    },
    avgSignOffTurnaroundDays() {
      let totalDays = 0;
      let matched = 0;
      this.logbookEntries.forEach((e) => {
        const completedDate = parseSignOffDate(e.completed);
        const signOffDate = parseSignOffDate(e.ins_so);
        if (!completedDate || !signOffDate || signOffDate < completedDate) return;
        totalDays += (signOffDate - completedDate) / (1000 * 60 * 60 * 24);
        matched += 1;
      });
      return matched ? totalDays / matched : null;
    },
    avgSignOffTurnaroundDisplay() {
      return this.avgSignOffTurnaroundDays !== null ? `${this.avgSignOffTurnaroundDays.toFixed(1)} days` : '—';
    },
    turnaroundBuckets() {
      const counts = TURNAROUND_BUCKETS.map(() => 0);
      this.logbookEntries.forEach((e) => {
        const completedDate = parseSignOffDate(e.completed);
        const signOffDate = parseSignOffDate(e.ins_so);
        if (!completedDate || !signOffDate || signOffDate < completedDate) return;
        const days = (signOffDate - completedDate) / (1000 * 60 * 60 * 24);
        const idx = TURNAROUND_BUCKETS.findIndex((b) => days >= b.min && days < b.max);
        if (idx !== -1) counts[idx] += 1;
      });
      return TURNAROUND_BUCKETS.map((b, i) => ({ label: b.label, value: counts[i], min: b.min, max: b.max }));
    },
    // Approximation: attributed to whoever originally signed the entry off
    instructorRevocationCounts() {
      const counts = new Map();
      this.logbookEntries.forEach((e) => {
        if (!e.is_revoked || !e.instructor_id) return;
        counts.set(e.instructor_id, (counts.get(e.instructor_id) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([instructorId, value]) => ({ label: this.instructorLabelFor(instructorId), value, instructor_id: instructorId }))
        .sort((a, b) => b.value - a.value);
    },
    taskCompletionStats() {
      const stats = new Map();
      this.logbookEntries.forEach((e) => {
        if (!stats.has(e.task_id)) stats.set(e.task_id, { total: 0, completed: 0 });
        const s = stats.get(e.task_id);
        s.total += 1;
        if (e.completed && !e.is_revoked) s.completed += 1;
      });
      return stats;
    },
    // Per-task completion %
    taskCompletionRates() {
      const rows = [];
      this.taskCompletionStats.forEach((stats, taskId) => {
        if (stats.total < MIN_TASK_SAMPLE) return;
        const pct = (stats.completed / stats.total) * 100;
        rows.push({ label: this.taskLabelFor(taskId), value: Math.round(pct), task_id: taskId });
      });
      return rows;
    },
    // The tasks trainees complete least and most often
    lowestCompletionTasks() {
      return [...this.taskCompletionRates].sort((a, b) => a.value - b.value).slice(0, 10);
    },
    highestCompletionTasks() {
      return [...this.taskCompletionRates].sort((a, b) => b.value - a.value).slice(0, 10);
    },
    // Logbooks whose session is close to (or past) its end date, and still have pending tasks
    entryLocationSegments() {
      let classroomOnly = 0;
      let aircraftOnly = 0;
      let both = 0;
      let neither = 0;
      this.logbookEntries.forEach((e) => {
        if (!e.completed || e.is_revoked) return;
        const classroom = !!e.entry_P1;
        const aircraft = !!e.entry_P2;
        if (classroom && aircraft) both += 1;
        else if (classroom) classroomOnly += 1;
        else if (aircraft) aircraftOnly += 1;
        else neither += 1;
      });
      const segments = [
        { label: 'Classroom', value: classroomOnly, tone: 'accent', id: 'classroom' },
        { label: 'Aircraft', value: aircraftOnly, tone: 'neutral', id: 'aircraft' },
      ];
      if (both > 0) segments.push({ label: 'Both', value: both, tone: 'good', id: 'both' });
      if (neither > 0) segments.push({ label: 'Unspecified', value: neither, tone: 'warning', id: 'neither' });
      return segments;
    },
    atRiskLogbooks() {
      const now = new Date();
      const projectById = new Map(this.projects.map((p) => [p.project_id, p]));
      const logbookById = new Map(this.logbooks.map((lb) => [lb.logbook_id, lb]));

      return this.perLogbookSummary
        .map((row) => {
          if (row.pending <= 0) return null;
          const lb = logbookById.get(row.logbook_id);
          const project = lb ? projectById.get(lb.project_id) : null;
          if (!project || !project.end_date) return null;

          const endDate = parseSignOffDate(project.end_date);
          if (!endDate) return null;
          const daysUntilEnd = Math.round((endDate - now) / (1000 * 60 * 60 * 24));
          if (daysUntilEnd > RISK_WINDOW_DAYS) return null;

          const activityDates = this.logbookEntries
            .filter((e) => e.logbook_id === row.logbook_id)
            .flatMap((e) => [parseSignOffDate(e.completed), parseSignOffDate(e.ins_so)])
            .filter(Boolean);
          const lastActivity = activityDates.length ? new Date(Math.max(...activityDates)) : null;
          const daysSinceActivity = lastActivity ? (now - lastActivity) / (1000 * 60 * 60 * 24) : Infinity;
          if (daysSinceActivity < STALE_ACTIVITY_DAYS) return null;

          return {
            logbook_id: row.logbook_id,
            trainee: lb ? this.traineeNameFor(lb.trainee_id) : row.label,
            sessionName: project.name,
            sessionEnds: endDate.toLocaleDateString(),
            daysUntilEnd,
            pending: row.pending,
            lastActivity: lastActivity ? lastActivity.toLocaleDateString() : 'Never',
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.daysUntilEnd - b.daysUntilEnd);
    },
    atRiskNote() {
      return `Session ending within ${RISK_WINDOW_DAYS} days, no activity in ${STALE_ACTIVITY_DAYS}+ days`;
    },
    atRiskColumns() {
      return [
        { label: 'Trainee', field: 'trainee', sortable: true },
        { label: 'Session', field: 'sessionName', sortable: true },
        { label: 'Session ends', field: 'sessionEnds', sortable: true },
        { label: 'Days left', field: 'daysUntilEnd', sortable: true },
        { label: 'Pending tasks', field: 'pending', sortable: true },
        { label: 'Last activity', field: 'lastActivity', sortable: true },
      ];
    },

    // ---------- Per-logbook-trainee drill-down ----------
    perLogbookSummary() {
      const productById = new Map(this.products.map((p) => [p.product_id, p]));
      const projectById = new Map(this.projects.map((p) => [p.project_id, p]));
      const now = new Date();
      const statsByLogbook = new Map();

      this.logbookEntries.forEach((e) => {
        if (!statsByLogbook.has(e.logbook_id)) {
          statsByLogbook.set(e.logbook_id, { total: 0, completed: 0, signedOff: 0, revoked: 0, classroom: 0, aircraft: 0 });
        }
        const stats = statsByLogbook.get(e.logbook_id);
        stats.total += 1;
        if (e.is_revoked) stats.revoked += 1;
        else if (e.completed) {
          stats.completed += 1;
          // Per-entry P1/P2
          if (e.entry_P1 && !e.entry_P2) stats.classroom += 1;
          else if (e.entry_P2 && !e.entry_P1) stats.aircraft += 1;
        }
        if (e.ins_so) stats.signedOff += 1;
      });

      return this.logbooks.map((lb) => {
        const stats = statsByLogbook.get(lb.logbook_id) || { total: 0, completed: 0, signedOff: 0, revoked: 0, classroom: 0, aircraft: 0 };
        const project = projectById.get(lb.project_id);
        const product = productById.get(lb.product_id);
        const passRate = product && product.pass_rate !== null && product.pass_rate !== undefined
          ? Number(product.pass_rate)
          : null;

        // Actual completion % vs. how much of the session's own timeline has already elapsed 
        const progressPct = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : null;
        let expectedProgressPct = null;
        if (project) {
          const start = parseSignOffDate(project.start_date);
          const end = parseSignOffDate(project.end_date);
          if (start && end && end > start) {
            const elapsedRatio = Math.min(1, Math.max(0, (now - start) / (end - start)));
            expectedProgressPct = Math.round(elapsedRatio * (passRate ?? 100));
          }
        }
        const paceGap = progressPct !== null && expectedProgressPct !== null ? expectedProgressPct - progressPct : null;
        const passRateMet = passRate !== null && progressPct !== null && progressPct >= passRate;

        let paceStatus;
        if (progressPct === null) paceStatus = 'No tasks assigned';
        else if (stats.completed === stats.total) paceStatus = 'Complete';
        else if (passRateMet) paceStatus = 'Pass rate met';
        else if (paceGap === null) paceStatus = 'In progress';
        else if (paceGap > PACE_BEHIND_THRESHOLD) paceStatus = 'Behind pace';
        else if (paceGap < -PACE_BEHIND_THRESHOLD) paceStatus = 'Ahead of pace';
        else paceStatus = 'On track';

        return {
          logbook_id: lb.logbook_id,
          project_id: lb.project_id,
          label: lb.trainee_full_name || lb.name || `Logbook ${lb.logbook_id}`,
          trainee: this.traineeNameFor(lb.trainee_id),
          product_name: product ? product.name : `${this.$labels.product} ${lb.product_id}`,
          project_name: project ? project.name : `${this.$labels.project} ${lb.project_id}`,
          status: lb.status === 'inactive' ? 'Locked' : 'Active',
          total: stats.total,
          completed: stats.completed,
          signedOff: stats.signedOff,
          pending: stats.total - stats.completed - stats.revoked,
          revoked: stats.revoked,
          // % of this logbook's completed tasks performed in the classroom
          // vs. on the aircraft - null (rendered "—") until anything's been
          // completed. Won't necessarily sum to 100 if any completed entry
          // has both/neither `entry_P1`/`entry_P2` set - see the comment
          // above where `stats.classroom`/`stats.aircraft` are tallied.
          classroomPct: stats.completed > 0 ? Math.round((stats.classroom / stats.completed) * 100) : null,
          aircraftPct: stats.completed > 0 ? Math.round((stats.aircraft / stats.completed) * 100) : null,
          passRate,
          progressPct,
          expectedProgressPct,
          paceGap,
          paceStatus,
        };
      });
    },
    filteredLogbookSummary() {
      const query = this.logbookSearchQuery.trim().toLowerCase();
      if (!query) return this.perLogbookSummary;
      return this.perLogbookSummary.filter((row) => row.label.toLowerCase().includes(query));
    },
    logbookColumns() {
      return [
        { label: 'Trainee / Logbook', field: 'label', sortable: true, thClass: 'vgt-logbook-name-column', tdClass: 'vgt-logbook-name-column' },
        { label: 'Status', field: 'status', sortable: true, thClass: 'vgt-catalog-column', tdClass: 'vgt-catalog-column' },
        { label: 'Entries', field: 'total', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'Completed', field: 'completed', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'Signed off', field: 'signedOff', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'Revoked', field: 'revoked', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'Classroom %', field: 'classroomPct', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'Aircraft %', field: 'aircraftPct', sortable: true, thClass: 'vgt-id-column', tdClass: 'vgt-id-column' },
        { label: 'Actions', field: 'actions', sortable: false, thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
      ];
    },

    // ---------- Trainee progress by session (Sessions tab) ----------
    sessionFilterOptions() {
      return [...this.projects]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((p) => ({ value: p.project_id, label: p.name }));
    },
    filteredSessionProgress() {
      const query = this.sessionProgressQuery.trim().toLowerCase();
      return this.perLogbookSummary
        .filter((row) => !this.sessionProgressFilter || row.project_id === this.sessionProgressFilter)
        .filter((row) => !query || row.trainee.toLowerCase().includes(query))
        // Worst pace gap first, so trainees falling behind surface immediately.
        .sort((a, b) => (b.paceGap ?? -Infinity) - (a.paceGap ?? -Infinity));
    },
    behindPaceCount() {
      return this.perLogbookSummary.filter((row) => row.paceStatus === 'Behind pace').length;
    },
    selectedSessionName() {
      const project = this.projects.find((p) => p.project_id === this.sessionProgressFilter);
      return project ? project.name : '';
    },
    // Each trainee's current completion % against the pass rate - green at/above it, orange below
    sessionPassRateChart() {
      if (!this.sessionProgressFilter) return null;
      const project = this.projects.find((p) => p.project_id === this.sessionProgressFilter);
      if (!project) return null;

      const rows = this.perLogbookSummary.filter((r) => r.project_id === this.sessionProgressFilter && r.total > 0);
      if (!rows.length) return null;
      const passRate = rows[0].passRate; // same product for every logbook in a session

      const items = rows
        .map((row) => ({
          label: row.trainee,
          value: row.progressPct ?? 0,
          logbook_id: row.logbook_id,
          tone: passRate === null ? 'accent' : (row.progressPct ?? 0) >= passRate ? 'good' : 'warning',
        }))
        .sort((a, b) => b.value - a.value);

      const product = this.products.find((p) => p.product_id === project.product_id);
      return { items, passRate, productName: product ? product.name : `${this.$labels.product}` };
    },
    // Cumulative completion % per trainee for the selected session
    sessionProgressChart() {
      if (!this.sessionProgressFilter) return null;
      const project = this.projects.find((p) => p.project_id === this.sessionProgressFilter);
      if (!project) return null;

      const start = parseSignOffDate(project.start_date);
      const end = parseSignOffDate(project.end_date);
      if (!start || !end || end <= start) return null;

      const rows = this.perLogbookSummary.filter((r) => r.project_id === this.sessionProgressFilter && r.total > 0);
      if (!rows.length) return null;

      const today = new Date();
      const rangeEnd = today > end ? today : end; // keep charting progress made after a missed deadline

      const entriesByLogbook = new Map();
      this.logbookEntries.forEach((e) => {
        if (!entriesByLogbook.has(e.logbook_id)) entriesByLogbook.set(e.logbook_id, []);
        entriesByLogbook.get(e.logbook_id).push(e);
      });

      const seriesInputs = rows.map((row) => ({
        key: String(row.logbook_id),
        dates: (entriesByLogbook.get(row.logbook_id) || [])
          .map((e) => parseSignOffDate(e.completed))
          .filter(Boolean),
      }));

      const countBuckets = buildCumulativeTrend(seriesInputs, this.sessionProgressGranularity, start, rangeEnd);
      if (!countBuckets.length) return null;

      const totalByKey = new Map(rows.map((row) => [String(row.logbook_id), row.total]));
      const passRate = rows[0].passRate; // same product for every logbook in a session
      const buckets = countBuckets.map((b) => {
        const pctBucket = { label: b.label, start: b.start, end: b.end };
        rows.forEach((row) => {
          const key = String(row.logbook_id);
          pctBucket[key] = Math.round((b[key] / totalByKey.get(key)) * 100);
        });
        const elapsedRatio = Math.min(1, Math.max(0, (b.end - start) / (end - start)));
        // Rises to the pass rate
        pctBucket.expected = Math.round(elapsedRatio * (passRate ?? 100));
        return pctBucket;
      });

      const series = rows.map((row) => ({
        key: String(row.logbook_id),
        label: row.trainee,
        logbook_id: row.logbook_id,
        behind: row.paceStatus === 'Behind pace',
        ahead: ['Ahead of pace', 'Pass rate met', 'Complete'].includes(row.paceStatus),
      }));
      series.push({ key: 'expected', label: 'Expected pace', isTarget: true });

      return { series, buckets };
    },
    sessionProgressColumns() {
      return [
        { label: `${this.$labels.project}`, field: 'project_name', sortable: true },
        { label: 'Trainee', field: 'trainee', sortable: true, thClass: 'vgt-user-name-column', tdClass: 'vgt-user-name-column' },
        { label: 'Progress', field: 'progressPct', sortable: true, thClass: 'vgt-pace-column', tdClass: 'vgt-pace-column' },
        { label: 'Pace', field: 'paceStatus', sortable: true, thClass: 'vgt-pace-column', tdClass: 'vgt-pace-column' },
        { label: 'Actions', field: 'actions', sortable: false, thClass: 'vgt-actions-column', tdClass: 'vgt-actions-column' },
      ];
    },
    // "Trainee completed" events are this specific trainee's completions;
    // "Instructor signed off" is logbookentry level
    selectedLogbookEvents() {
      if (!this.selectedLogbookForTimeline) return [];
      const taskById = new Map(this.tasks.map((t) => [t.task_id, t]));
      const events = [];

      this.logbookEntries
        .filter((e) => e.logbook_id === this.selectedLogbookForTimeline.logbook_id)
        .forEach((e) => {
          const task = taskById.get(e.task_id);
          const taskLabel = task ? `Ch ${task.chapt_no} · Task ${task.task_no}` : `Task ${e.task_id}`;

          const completedDate = parseSignOffDate(e.completed);
          if (completedDate) {
            events.push({ date: completedDate, type: 'Trainee completed', taskLabel, instructorName: '' });
          }

          const signOffDate = parseSignOffDate(e.ins_so);
          if (signOffDate) {
            const instructor = this.users.find((u) => u.user_id === e.instructor_id);
            events.push({
              date: signOffDate,
              type: 'Instructor signed off',
              taskLabel,
              instructorName: instructor ? `${instructor.first_name} ${instructor.last_name}` : '',
            });
          }
        });

      return events.sort((a, b) => b.date - a.date);
    },
    selectedLogbookSkippedCount() {
      if (!this.selectedLogbookForTimeline) return 0;
      let skipped = 0;
      this.logbookEntries
        .filter((e) => e.logbook_id === this.selectedLogbookForTimeline.logbook_id)
        .forEach((e) => {
          if (e.completed && !parseSignOffDate(e.completed)) skipped += 1;
          if (e.ins_so && !parseSignOffDate(e.ins_so)) skipped += 1;
        });
      return skipped;
    },
    selectedLogbookSegments() {
      if (!this.selectedLogbookForTimeline) return [];
      const row = this.selectedLogbookForTimeline;
      return [
        { label: 'Completed', value: row.completed, tone: 'good' },
        { label: 'Pending', value: row.pending, tone: 'warning' },
        { label: 'Revoked', value: row.revoked, tone: 'critical' },
      ];
    },

    // ---------- Evaluations ----------
    totalEvaluations() {
      return this.evaluations.length;
    },
    passedEvaluationsCount() {
      return this.evaluations.filter((e) => this.evaluationStatusLabel(e) === 'Passed').length;
    },
    remedialRequiredCount() {
      return this.evaluations.filter((e) => this.evaluationStatusLabel(e) === 'Remedial required').length;
    },
    failedEvaluationsCount() {
      return this.evaluations.filter((e) => this.evaluationStatusLabel(e) === 'Failed').length;
    },
    pendingEvaluationsCount() {
      return this.evaluations.filter((e) => {
        const status = this.evaluationStatusLabel(e);
        return status === 'In progress' || status === 'Awaiting signature';
      }).length;
    },
    evaluationStatusSegments() {
      const counts = new Map();
      this.evaluations.forEach((e) => {
        const status = this.evaluationStatusLabel(e);
        if (!status) return;
        counts.set(status, (counts.get(status) || 0) + 1);
      });
      return Array.from(counts.entries()).map(([status, value]) => ({
        label: status,
        value,
        tone: this.evaluationTone(status),
        id: status,
      }));
    },
    evaluationsByInstructor() {
      const counts = new Map();
      this.evaluations.forEach((e) => {
        if (!e.instructor_id) return;
        counts.set(e.instructor_id, (counts.get(e.instructor_id) || 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([instructorId, value]) => ({ label: this.instructorLabelFor(instructorId), value, instructor_id: instructorId }))
        .sort((a, b) => b.value - a.value);
    },
    selectedEvaluationSessionName() {
      const project = this.projects.find((p) => p.project_id === this.evaluationSessionFilter);
      return project ? project.name : '';
    },
    // Outcome breakdown for just the selected session
    evaluationSessionOutcomeSegments() {
      if (!this.evaluationSessionFilter) return [];
      const logbookById = new Map(this.logbooks.map((lb) => [lb.logbook_id, lb]));
      const counts = new Map();

      this.evaluations.forEach((e) => {
        const lb = logbookById.get(e.logbook_id);
        if (!lb || lb.project_id !== this.evaluationSessionFilter) return;
        const status = this.evaluationStatusLabel(e);
        if (!status) return;
        counts.set(status, (counts.get(status) || 0) + 1);
      });

      return Array.from(counts.entries()).map(([status, value]) => ({
        label: status,
        value,
        tone: this.evaluationTone(status),
        id: status,
      }));
    },
    // Which of the 9 fixed competency criteria trainees most often get
    // marked unsatisfactory on, across every submitted evaluation.
    criteriaFailureRates() {
      const stats = EVALUATION_CRITERIA.map(() => ({ total: 0, unsatisfactory: 0 }));
      this.evaluations.forEach((e) => {
        if (!e.criteria) return;
        Object.entries(e.criteria).forEach(([criterionId, result]) => {
          const idx = Number(criterionId) - 1;
          if (idx < 0 || idx >= stats.length || !result) return;
          stats[idx].total += 1;
          if (result === 'unsatisfactory') stats[idx].unsatisfactory += 1;
        });
      });
      return stats
        .map((s, i) => ({
          label: EVALUATION_CRITERIA[i],
          value: s.total ? Math.round((s.unsatisfactory / s.total) * 100) : 0,
          total: s.total,
          criterionId: i + 1,
        }))
        .filter((row) => row.total >= MIN_CRITERION_SAMPLE)
        .sort((a, b) => b.value - a.value);
    },
    evaluationsTrendSeries() {
      return [
        { key: 'passed', label: 'Passed', colorVar: '--series-1' },
        { key: 'needsAttention', label: 'Remedial / Failed', colorVar: '--series-2' },
      ];
    },
    evaluationsTrend() {
      const records = [];
      this.evaluations.forEach((e) => {
        const date = parseSignOffDate(e.date);
        if (!date) return;
        const status = this.evaluationStatusLabel(e);
        if (status === 'Passed') records.push({ date, seriesKey: 'passed' });
        else if (status === 'Remedial required' || status === 'Failed') records.push({ date, seriesKey: 'needsAttention' });
      });
      return buildTrend(records, this.evaluationsGranularity);
    },
    evaluationDrilldownColumns() {
      return [
        { label: 'Trainee', field: 'trainee', sortable: true },
        { label: `${this.$labels.project}`, field: 'sessionName', sortable: true },
        { label: 'Type', field: 'type', sortable: true },
        { label: 'Result', field: 'status', sortable: true },
        { label: 'Instructor', field: 'instructor', sortable: true },
        { label: 'Date', field: 'dateLabel', sortable: true },
      ];
    },
    // The "current" evaluation per logbook
    currentEvaluationByLogbook() {
      const grouped = new Map();
      this.evaluations.forEach((e) => {
        if (!grouped.has(e.logbook_id)) grouped.set(e.logbook_id, []);
        grouped.get(e.logbook_id).push(e);
      });
      const current = new Map();
      grouped.forEach((list, logbookId) => {
        const continuous = list.find((e) => e.evaluation_type === 'continuous') || null;
        const remedial = list.find((e) => e.evaluation_type === 'remedial') || null;
        current.set(logbookId, remedial || continuous || null);
      });
      return current;
    },
    // Trainees whose current evaluation needs an examiner's attention -
    // failed, needs a remedial evaluation, or has been sitting unsigned.
    evaluationsNeedingAttention() {
      const priority = { Failed: 0, 'Remedial required': 1, 'Awaiting signature': 2 };
      const rows = [];
      this.currentEvaluationByLogbook.forEach((evaluation, logbookId) => {
        if (!evaluation) return;
        const status = this.evaluationStatusLabel(evaluation);
        if (!(status in priority)) return;
        rows.push({ logbook_id: logbookId, ...this.evaluationDrilldownRow(evaluation) });
      });
      return rows.sort((a, b) => priority[a.status] - priority[b.status]);
    },
    evaluationAttentionColumns() {
      return [
        { label: 'Trainee', field: 'trainee', sortable: true },
        { label: `${this.$labels.project}`, field: 'sessionName', sortable: true },
        { label: 'Status', field: 'status', sortable: true },
        { label: 'Instructor', field: 'instructor', sortable: true },
        { label: 'Evaluation date', field: 'dateLabel', sortable: true },
        { label: 'Actions', field: 'actions', sortable: false },
      ];
    },

    // ---------- Overview: recent activity feed ----------
    recentActivityFeed() {
      const items = [];

      this.logbookEntries.forEach((e) => {
        const logbookLabel = this.logbookLabelFor(e.logbook_id);
        const taskLabel = this.taskLabelFor(e.task_id);

        const completedDate = parseSignOffDate(e.completed);
        if (completedDate && !e.is_revoked) {
          items.push({
            id: `completed-${e.logbook_id}-${e.task_id}`,
            date: completedDate,
            icon: 'circle-check',
            tone: 'good',
            text: `Trainee Signed Off ${taskLabel}`,
            subtext: logbookLabel,
            clickAction: { type: 'logbook', logbookId: e.logbook_id },
          });
        }

        const signOffDate = parseSignOffDate(e.ins_so);
        if (signOffDate) {
          items.push({
            id: `signoff-${e.logbook_id}-${e.task_id}`,
            date: signOffDate,
            icon: 'clipboard-check',
            text: `${this.instructorLabelFor(e.instructor_id)} signed off ${taskLabel}`,
            subtext: logbookLabel,
            clickAction: { type: 'logbook', logbookId: e.logbook_id },
          });
        }
      });

      this.evaluations.forEach((e) => {
        const date = parseSignOffDate(e.date);
        const status = this.evaluationStatusLabel(e);
        if (!date || !['Passed', 'Remedial required', 'Failed'].includes(status)) return;
        const lb = this.logbooks.find((l) => l.logbook_id === e.logbook_id);
        const project = lb ? this.projects.find((p) => p.project_id === lb.project_id) : null;
        items.push({
          id: `eval-${e.logbook_id}-${e.evaluation_type}-${date.getTime()}`,
          date,
          icon: 'clipboard-list',
          tone: this.evaluationTone(status),
          text: `${this.traineeNameFor(e.trainee_id)} — ${status}`,
          subtext: project ? project.name : this.logbookLabelFor(e.logbook_id),
          clickAction: { type: 'logbook', logbookId: e.logbook_id },
        });
      });

      return items
        .sort((a, b) => b.date - a.date)
        .slice(0, 8)
        .map((item) => ({ ...item, meta: this.relativeTimeLabel(item.date) }));
    },
    // Newest entries in the task catalog
    recentTasksAdded() {
      const productNameById = new Map(this.products.map((p) => [p.product_id, p.name]));
      return [...this.tasks]
        .sort((a, b) => b.task_id - a.task_id)
        .slice(0, 8)
        .map((t) => ({
          id: `task-${t.task_id}`,
          icon: 'plus',
          text: `Ch ${t.chapt_no || '—'} · Task ${t.task_no} · ${t.description}`,
          subtext: productNameById.get(t.product_id) || `${this.$labels.product} ${t.product_id}`,
          meta: t.manob || '—',
          clickAction: { type: 'product', productId: t.product_id },
        }));
    },
  },
  async mounted() {
    await this.fetchOverviewData();
    this.fetchLogbookEntries();
    this.fetchEvaluations();
  },
  methods: {
    getHeaders() {
      const token = localStorage.getItem('token');
      return { Authorization: `Bearer ${token}` };
    },
    openLogbookTimeline(row) {
      this.selectedLogbookForTimeline = row;
    },
    closeLogbookTimeline() {
      this.selectedLogbookForTimeline = null;
    },
    selectTab(key) {
      this.activeTab = key;
      this.drilldown = null;
      this.$nextTick(() => window.dispatchEvent(new Event('resize')));
      // "Sessions" and "Evaluations" need entries too, for their tables' drill-through.
      if ((key === 'logbooks' || key === 'projects' || key === 'evaluations') && !this.entriesLoaded && !this.entriesLoading) {
        this.fetchLogbookEntries();
      }
      if (key === 'evaluations' && !this.evaluationsLoaded && !this.evaluationsLoading) {
        this.fetchEvaluations();
      }
    },

    // ---------- Drill-down ----------
    showDrilldown(title, rows, columns) {
      this.drilldown = { title, rows, columns };
    },
    clearDrilldown() {
      this.drilldown = null;
    },
    viewProductDetails(productId) {
      localStorage.setItem('product_id', productId);
      this.showProductDetails = true;
    },
    closeProductDetails() {
      this.showProductDetails = false;
    },
    logbookLabelFor(logbookId) {
      const lb = this.logbooks.find((l) => l.logbook_id === logbookId);
      return lb ? (lb.trainee_full_name || lb.name || `Logbook ${logbookId}`) : `Logbook ${logbookId}`;
    },
    taskLabelFor(taskId) {
      const task = this.tasks.find((t) => t.task_id === taskId);
      return task ? `ATA ${task.chapt_no} · Task ${task.task_no} · ${task.description}` : `Task ${taskId}`;
    },
    // Shared row shape for every task-drilldown table
    taskDrilldownRow(t) {
      return {
        chapter: t.chapt_no || 'Unspecified',
        task_no: t.task_no,
        type: t.manob || '—',
        description: t.description || '—',
      };
    },
    instructorLabelFor(instructorId) {
      const user = this.users.find((u) => u.user_id === instructorId);
      return user ? `${user.first_name} ${user.last_name}` : '—';
    },
    traineeNameFor(traineeId) {
      const user = this.users.find((u) => u.user_id === traineeId);
      return user ? `${user.last_name}, ${user.first_name}` : '—';
    },
    // Shared row shape for every user-drilldown table
    userDrilldownRow(u) {
      return {
        name: `${u.first_name} ${u.last_name}`,
        email: u.email,
        roles: (u.roles || []).map((r) => ROLE_LABELS[r] || r).join(', '),
        status: u.is_active ? 'Active' : 'Inactive',
      };
    },
    // "Recent activity" / "Recently added tasks"
    handleActivityClick(action) {
      if (!action) return;
      if (action.type === 'logbook') {
        const row = this.perLogbookSummary.find((r) => r.logbook_id === action.logbookId);
        if (row) this.openLogbookTimeline(row);
      } else if (action.type === 'product') {
        this.viewProductDetails(action.productId);
      }
    },
    relativeTimeLabel(date) {
      const diffMs = new Date() - date;
      const diffMin = Math.round(diffMs / 60000);
      if (diffMin < 1) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.round(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      return date.toLocaleDateString();
    },
    paceStatusTone(status) {
      switch (status) {
        case 'Complete':
        case 'Pass rate met':
        case 'Ahead of pace':
        case 'On track':
          return 'tone-good';
        case 'Behind pace':
          return 'tone-critical';
        default:
          return 'tone-neutral';
      }
    },
    // Per-record status label
    evaluationStatusLabel(evaluation) {
      if (!evaluation) return '';
      if (evaluation.is_submitted === 0) return 'In progress';
      if (evaluation.is_submitted === 1) return 'Awaiting signature';
      if (evaluation.is_submitted === 2) {
        if (evaluation.evaluation_type === 'continuous' && evaluation.result === 'successful') return 'Passed';
        if (evaluation.evaluation_type === 'continuous' && evaluation.result === 'remedial') return 'Remedial required';
        if (evaluation.evaluation_type === 'remedial' && evaluation.result === 'successful') return 'Passed';
        if (evaluation.evaluation_type === 'remedial' && evaluation.result === 'unsuccessful') return 'Failed';
        return 'Signed';
      }
      return '';
    },
    evaluationTone(status) {
      switch (status) {
        case 'Passed': return 'good';
        case 'Remedial required': return 'warning';
        case 'Failed': return 'critical';
        default: return 'neutral'; // In progress / Awaiting signature / Signed
      }
    },
    evaluationDrilldownRow(e) {
      const lb = this.logbooks.find((l) => l.logbook_id === e.logbook_id);
      const project = lb ? this.projects.find((p) => p.project_id === lb.project_id) : null;
      return {
        trainee: this.traineeNameFor(e.trainee_id),
        sessionName: project ? project.name : (lb ? `${this.$labels.project} ${lb.project_id}` : '—'),
        type: e.evaluation_type === 'remedial' ? 'Remedial' : 'Continuous',
        status: this.evaluationStatusLabel(e),
        instructor: this.instructorLabelFor(e.instructor_id),
        dateLabel: parseSignOffDate(e.date)?.toLocaleDateString() || '—',
      };
    },
    // Trainees needing attention
    openEvaluationLogbookTimeline(row) {
      const summaryRow = this.perLogbookSummary.find((r) => r.logbook_id === row.logbook_id);
      if (summaryRow) this.openLogbookTimeline(summaryRow);
    },
    // "Evaluation outcomes" donut segment -> evaluations with that status
    drillEvaluationStatus(segment) {
      const rows = this.evaluations
        .filter((e) => this.evaluationStatusLabel(e) === segment.id)
        .map((e) => this.evaluationDrilldownRow(e));
      this.showDrilldown(`Evaluations — ${segment.label}`, rows, this.evaluationDrilldownColumns);
    },
    // "Evaluations by instructor" bar
    drillEvaluationsByInstructor(item) {
      const rows = this.evaluations
        .filter((e) => e.instructor_id === item.instructor_id)
        .map((e) => this.evaluationDrilldownRow(e));
      this.showDrilldown(`${item.label}'s evaluations`, rows, this.evaluationDrilldownColumns);
    },
    // "Evaluations by session" bar
    drillEvaluationSessionStatus(segment) {
      const logbookById = new Map(this.logbooks.map((lb) => [lb.logbook_id, lb]));
      const rows = this.evaluations
        .filter((e) => {
          const lb = logbookById.get(e.logbook_id);
          return lb && lb.project_id === this.evaluationSessionFilter && this.evaluationStatusLabel(e) === segment.id;
        })
        .map((e) => this.evaluationDrilldownRow(e));
      this.showDrilldown(`${this.selectedEvaluationSessionName} — ${segment.label}`, rows, this.evaluationDrilldownColumns);
    },
    // "Criteria failure rate" bar
    drillCriterionFailures(item) {
      const rows = this.evaluations
        .filter((e) => e.criteria && e.criteria[item.criterionId] === 'unsatisfactory')
        .map((e) => this.evaluationDrilldownRow(e));
      this.showDrilldown(`Marked unsatisfactory — ${item.label}`, rows, this.evaluationDrilldownColumns);
    },
    // "Evaluations over time" line chart point
    drillEvaluationsTrend({ series, bucket }) {
      const wantedStatuses = series.key === 'passed' ? ['Passed'] : ['Remedial required', 'Failed'];
      const rows = this.evaluations
        .filter((e) => {
          const date = parseSignOffDate(e.date);
          return date && date >= bucket.start && date < bucket.end && wantedStatuses.includes(this.evaluationStatusLabel(e));
        })
        .map((e) => this.evaluationDrilldownRow(e));
      this.showDrilldown(`${series.label} — ${bucket.label}`, rows, this.evaluationDrilldownColumns);
    },
    drillProductTasks(item) {
      this.viewProductDetails(item.product_id);
    },
    // "{Project}s by {product}" bar
    drillProjectsForProduct(item) {
      const rows = this.projects
        .filter((p) => p.product_id === item.product_id)
        .map((p) => ({ name: p.name, status: p.locked ? 'Locked' : 'Active' }));
      this.showDrilldown(`${this.$labels.project}s in ${item.label}`, rows, this.projectDrilldownColumns);
    },
    // "Users by role" bar
    drillUsersByRole(item) {
      const rows = this.users
        .filter((u) => Array.isArray(u.roles) && u.roles.includes(item.role))
        .map((u) => this.userDrilldownRow(u));
      this.showDrilldown(`Users — ${item.label}`, rows, this.userDrilldownColumns);
    },
    // "Tasks by chapter" bar
    drillTasksByChapter(item) {
      const rows = this.tasks
        .filter((t) => (t.chapt_no || 'Unspecified') === item.chapter)
        .map((t) => this.taskDrilldownRow(t));
      this.showDrilldown(item.label, rows, this.taskDrilldownColumns);
    },
    // "Logbooks with the most entries" bar
    drillLogbookFromChart(item) {
      const row = this.perLogbookSummary.find((r) => r.logbook_id === item.logbook_id);
      if (row) this.openLogbookTimeline(row);
    },
    // "Cumulative progress" session chart point
    drillSessionProgressPoint({ series }) {
      if (!series.logbook_id) return;
      const row = this.perLogbookSummary.find((r) => r.logbook_id === series.logbook_id);
      if (row) this.openLogbookTimeline(row);
    },
    // "Progress vs. pass rate" session bar
    drillSessionPassRateBar(item) {
      const row = this.perLogbookSummary.find((r) => r.logbook_id === item.logbook_id);
      if (row) this.openLogbookTimeline(row);
    },
    // "Instructors by sign-offs" bar -> that instructor's sign-off entries
    drillInstructorEntries(item) {
      const rows = this.logbookEntries
        .filter((e) => e.instructor_id === item.instructor_id && parseSignOffDate(e.ins_so))
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: item.label,
          dateLabel: parseSignOffDate(e.ins_so)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`${item.label}'s sign-offs`, rows, this.entryDrilldownColumns);
    },
    // "Instructors by tasks enabled" bar -> that instructor's enabled entries
    drillInstructorEnables(item) {
      const rows = this.logbookEntries
        .filter((e) => e.enabled && e.ins_enabled_id === item.instructor_id)
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: item.label,
          dateLabel: parseSignOffDate(e.selected_date)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`${item.label}'s assigned tasks`, rows, this.entryDrilldownColumns);
    },
    // "Task enablements over time" line chart point -> entries enabled in that bucket
    drillEnabledBucket({ bucket }) {
      const rows = this.logbookEntries
        .filter((e) => {
          if (!e.enabled) return false;
          const d = parseSignOffDate(e.selected_date);
          return d && d >= bucket.start && d < bucket.end;
        })
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: this.instructorLabelFor(e.ins_enabled_id),
          dateLabel: parseSignOffDate(e.selected_date)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`Tasks assigned — ${bucket.label}`, rows, this.entryDrilldownColumns);
    },
    // "Most/Least enabled tasks" bar -> that task's enabled entries (empty for a task that's never been enabled)
    drillTaskEnableCount(item) {
      const rows = this.logbookEntries
        .filter((e) => e.enabled && e.task_id === item.task_id)
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: item.label,
          instructorLabel: this.instructorLabelFor(e.ins_enabled_id),
          dateLabel: parseSignOffDate(e.selected_date)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`Tasks assigned — ${item.label}`, rows, this.entryDrilldownColumns);
    },
    // "Sign-offs over time" line chart point -> entries falling in that bucket, for that series
    drillSignOffBucket({ series, bucket }) {
      const dateField = series.key === 'completed' ? 'completed' : 'ins_so';
      const rows = this.logbookEntries
        .filter((e) => {
          const d = parseSignOffDate(e[dateField]);
          return d && d >= bucket.start && d < bucket.end;
        })
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: series.key === 'signedOff' ? this.instructorLabelFor(e.instructor_id) : '—',
          dateLabel: parseSignOffDate(e[dateField])?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`${series.label} — ${bucket.label}`, rows, this.entryDrilldownColumns);
    },
    // "Task coverage" / "Products" donut segment
    drillProductCoverage(segment) {
      const rows = this.products
        .filter((p) => {
          const hasTasks = (this.taskCountByProduct.get(p.product_id) || 0) > 0;
          return segment.id === 'with' ? hasTasks : !hasTasks;
        })
        .map((p) => ({ name: p.name, taskCount: this.taskCountByProduct.get(p.product_id) || 0 }));
      this.showDrilldown(segment.label, rows, this.productDrilldownColumns);
    },
    // "{Product}s by pass rate" bar
    drillProductPassRate(item) {
      this.viewProductDetails(item.product_id);
    },
    // "{Product}s added over time" line chart point
    drillProductsAddedBucket({ bucket }) {
      const rows = this.products
        .filter((p) => {
          const d = parseSignOffDate(p.created_at);
          return d && d >= bucket.start && d < bucket.end;
        })
        .map((p) => ({ name: p.name, taskCount: this.taskCountByProduct.get(p.product_id) || 0 }));
      this.showDrilldown(`${this.$labels.product}s added — ${bucket.label}`, rows, this.productDrilldownColumns);
    },
    // "{Project} status" donut segment
    drillProjectStatus(segment) {
      const rows = this.projects
        .filter((p) => (segment.id === 'active' ? !p.locked : p.locked))
        .map((p) => ({ name: p.name, status: p.locked ? 'Locked' : 'Active' }));
      this.showDrilldown(`${this.$labels.project}s — ${segment.label}`, rows, this.projectDrilldownColumns);
    },
    // "Account status" donut segment
    drillUserStatus(segment) {
      const rows = this.users
        .filter((u) => (segment.id === 'active' ? !!u.is_active : !u.is_active))
        .map((u) => this.userDrilldownRow(u));
      this.showDrilldown(`Users — ${segment.label}`, rows, this.userDrilldownColumns);
    },
    // "Trainees by license category" bar
    drillTraineesByCategory(item) {
      const rows = this.traineeUsers
        .filter((u) => (u.category && u.category.trim() ? u.category.trim() : 'Unspecified') === item.category)
        .map((u) => this.userDrilldownRow(u));
      this.showDrilldown(`Trainees — ${item.label}`, rows, this.userDrilldownColumns);
    },
    // "Trainee assignment" donut segment
    drillTraineeAssignment(segment) {
      const wantAssigned = segment.id === 'assigned';
      const rows = this.traineeUsers
        .filter((u) => this.traineeIdsWithLogbooks.has(u.user_id) === wantAssigned)
        .map((u) => this.userDrilldownRow(u));
      this.showDrilldown(`Trainees — ${segment.label}`, rows, this.userDrilldownColumns);
    },
    // "Busiest instructors" bar
    drillInstructorActivity(item) {
      const rows = [
        { activity: 'Sign-offs', count: item.breakdown.signOffs },
        { activity: 'Task enables', count: item.breakdown.enables },
        { activity: 'Evaluations', count: item.breakdown.evaluations },
      ];
      this.showDrilldown(`${item.label} — activity breakdown`, rows, this.instructorActivityColumns);
    },

    // "Task type breakdown" donut segment -> MT / AT / unspecified tasks
    drillTaskType(segment) {
      const rows = this.tasks
        .filter((t) => {
          if (segment.id === 'mt') return t.manob === 'MT';
          if (segment.id === 'at') return t.manob === 'AT';
          return t.manob !== 'MT' && t.manob !== 'AT';
        })
        .map((t) => this.taskDrilldownRow(t));
      this.showDrilldown(`Tasks — ${segment.label}`, rows, this.taskDrilldownColumns);
    },
    // "Tasks by category" donut segment
    drillTaskCategory(segment) {
      const rows = this.tasks
        .filter((t) => (segment.id === 'unspecified' ? !['AF', 'AV', 'PP'].includes(t.category) : t.category === segment.id))
        .map((t) => this.taskDrilldownRow(t));
      this.showDrilldown(`Tasks — ${segment.label}`, rows, this.taskDrilldownColumns);
    },
    // "Tasks by required skill" donut segment
    drillTaskSkill(segment) {
      const rows = this.tasks
        .filter((t) => (segment.id === 'unspecified' ? !['B1', 'B2', 'B1/B2'].includes(t.skill) : t.skill === segment.id))
        .map((t) => this.taskDrilldownRow(t));
      this.showDrilldown(`Tasks — ${segment.label}`, rows, this.taskDrilldownColumns);
    },
    // "Task catalog utilization" donut segmen
    drillTaskUtilization(segment) {
      const wantEver = segment.id === 'ever';
      const rows = this.taskEnableCounts
        .filter((t) => (wantEver ? t.value > 0 : t.value === 0))
        .map((t) => {
          const task = this.tasks.find((tk) => tk.task_id === t.task_id);
          return task ? this.taskDrilldownRow(task) : { chapter: 'Unspecified', task_no: t.task_id, type: '—', description: '—' };
        });
      this.showDrilldown(`Tasks — ${segment.label}`, rows, this.taskDrilldownColumns);
    },
    // "Task scope utilization" donut segment
    drillTaskScope(segment) {
      const rows = this.tasks
        .filter((t) => {
          const tokens = (t.scope || '').split(',').map((s) => s.trim()).filter(Boolean);
          return segment.id === 'unspecified' ? tokens.length === 0 : tokens.includes(segment.id);
        })
        .map((t) => this.taskDrilldownRow(t));
      this.showDrilldown(`Tasks — ${segment.label}`, rows, this.taskDrilldownColumns);
    },

    // "Entry completion" donut segment
    drillEntryCompletion(segment) {
      const rows = this.logbookEntries
        .filter((e) => {
          if (segment.id === 'revoked') return !!e.is_revoked;
          if (segment.id === 'completed') return !!e.completed && !e.is_revoked;
          return !e.completed && !e.is_revoked; // pending
        })
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: this.instructorLabelFor(e.instructor_id),
          dateLabel: parseSignOffDate(e.completed || e.ins_so)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`Entries — ${segment.label}`, rows, this.entryDrilldownColumns);
    },
    // "Sign-off turnaround" histogram bar -> entries whose turnaround fell in that bucket
    drillTurnaroundBucket(item) {
      const rows = this.logbookEntries
        .filter((e) => {
          const completedDate = parseSignOffDate(e.completed);
          const signOffDate = parseSignOffDate(e.ins_so);
          if (!completedDate || !signOffDate || signOffDate < completedDate) return false;
          const days = (signOffDate - completedDate) / (1000 * 60 * 60 * 24);
          return days >= item.min && days < item.max;
        })
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: this.instructorLabelFor(e.instructor_id),
          dateLabel: parseSignOffDate(e.ins_so)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`Sign-off turnaround — ${item.label}`, rows, this.entryDrilldownColumns);
    },

    // "Revocations by instructor" bar -> that instructor's revoked entries
    drillInstructorRevocations(item) {
      const rows = this.logbookEntries
        .filter((e) => e.is_revoked && e.instructor_id === item.instructor_id)
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: item.label,
          dateLabel: parseSignOffDate(e.ins_so || e.completed)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`Revoked entries — ${item.label}`, rows, this.entryDrilldownColumns);
    },

    // "Lowest completion tasks" bar -> logbooks still pending that task
    drillBottleneckTask(item) {
      const rows = this.logbookEntries
        .filter((e) => e.task_id === item.task_id && !e.completed && !e.is_revoked)
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: item.label,
          instructorLabel: '—',
          dateLabel: 'Not yet completed',
        }));
      this.showDrilldown(`Still pending — ${item.label}`, rows, this.entryDrilldownColumns);
    },
    // "Highest completion rate by task" bar
    drillTopCompletionTask(item) {
      const rows = this.logbookEntries
        .filter((e) => e.task_id === item.task_id && e.completed && !e.is_revoked)
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: item.label,
          instructorLabel: this.instructorLabelFor(e.instructor_id),
          dateLabel: parseSignOffDate(e.completed)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`Completed — ${item.label}`, rows, this.entryDrilldownColumns);
    },
    // "Completed tasks by training location" donut segment -> the completed
    // entries in that classroom/aircraft/both/unspecified bucket
    drillEntryLocation(segment) {
      const rows = this.logbookEntries
        .filter((e) => {
          if (!e.completed || e.is_revoked) return false;
          const classroom = !!e.entry_P1;
          const aircraft = !!e.entry_P2;
          switch (segment.id) {
            case 'classroom': return classroom && !aircraft;
            case 'aircraft': return aircraft && !classroom;
            case 'both': return classroom && aircraft;
            default: return !classroom && !aircraft;
          }
        })
        .map((e) => ({
          logbookLabel: this.logbookLabelFor(e.logbook_id),
          taskLabel: this.taskLabelFor(e.task_id),
          instructorLabel: this.instructorLabelFor(e.instructor_id),
          dateLabel: parseSignOffDate(e.completed)?.toLocaleDateString() || '',
        }));
      this.showDrilldown(`Completed tasks — ${segment.label}`, rows, this.entryDrilldownColumns);
    },
    async fetchOverviewData() {
      const headers = this.getHeaders();
      const requests = [
        { key: 'products', url: '/api/products', label: `${this.$labels.product}s` },
        { key: 'projects', url: '/api/projects', label: `${this.$labels.project}s` },
        { key: 'users', url: '/api/users', label: 'users' },
        { key: 'tasks', url: '/api/tasks', label: 'tasks' },
        { key: 'logbooks', url: '/api/logbooks', label: 'logbooks' },
      ];
      requests.forEach((r) => { this.loading[r.key] = true; });

      const results = await Promise.allSettled(
        requests.map((r) => axios.get(r.url, { headers }))
      );

      results.forEach((result, index) => {
        const { key, label } = requests[index];
        this.loading[key] = false;
        if (result.status === 'fulfilled') {
          this[key] = result.value.data;
        } else {
          console.error(`Error fetching ${key}:`, result.reason);
          this.loadErrors.push(label);
        }
      });
    },
    async fetchLogbookEntries() {
      if (!this.logbooks.length) {
        this.entriesLoaded = true;
        return;
      }
      this.entriesLoading = true;
      this.entriesError = null;
      const headers = this.getHeaders();

      const validLogbookIds = new Set(this.logbooks.map((lb) => lb.logbook_id));

      try {
        const res = await axios.get('/api/logbookentry', { headers });
        const allEntries = res.data || [];
        console.log('all', allEntries);

        // Only keep entries belonging to logbooks we actually care about
        this.logbookEntries = allEntries.filter((entry) =>
          validLogbookIds.has(entry.logbook_id)
        );
      } catch (err) {
        console.error('Failed to load logbook entries.', err);
        this.logbookEntries = [];
        this.entriesError = 'Entries could not be loaded.';
      }

      this.entriesLoading = false;
      this.entriesLoaded = true;
    },
    async fetchEvaluations() {
      if (!this.logbooks.length) {
        this.evaluationsLoaded = true;
        return;
      }
      this.evaluationsLoading = true;
      this.evaluationsError = null;
      const headers = this.getHeaders();

      const validLogbookIds = new Set(this.logbooks.map((lb) => lb.logbook_id));

      try {
        const res = await axios.get('/api/evaluations', { headers });
        const allEvaluations = res.data || [];
        this.evaluations = allEvaluations.filter((e) => validLogbookIds.has(e.logbook_id));
      } catch (err) {
        console.error('Failed to load evaluations.', err);
        this.evaluations = [];
        this.evaluationsError = 'Evaluations could not be loaded.';
      }

      this.evaluationsLoading = false;
      this.evaluationsLoaded = true;
    },
  },
};
</script>

<style scoped>
.dashboard-viz {
  color-scheme: light;
  --surface-1:      #fcfcfb;
  --page-plane:     #f2f2ef;
  --text-primary:   #0b0b0b;
  --text-secondary: #52514e;
  --text-muted:     #898781;
  --gridline:       #e1e0d9;
  --baseline:       #c3c2b7;
  --series-1:       #2a78d6;
  --series-1-tint:  rgba(42, 120, 214, 0.10);
  --series-2:       #eb6834;
  --status-good:      #0ca30c;
  --status-warning:   #fab219;
  --status-critical:  #d03b3b;
  --border: rgba(11, 11, 11, 0.10);
  background: var(--page-plane);
  min-height: 100vh;
  font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
}

.load-error-banner {
  margin: 20px 24px 0;
  padding: 10px 16px;
  border-radius: 8px;
  background: var(--error-bg);
  color: var(--error-text);
  border: 1px solid var(--error-border);
  border-left: 4px solid var(--error-accent);
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.dashboard-body {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  max-width: 1440px;
  margin: auto 48px;
  padding-top: 20px;
  padding-bottom: 30px;
  box-sizing: border-box;
}

/* ---------- Sidebar ---------- */

.dashboard-sidebar {
  flex: 0 0 216px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px;
  position: sticky;
  top: 20px;
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
}

.sidebar-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.sidebar-icon {
  width: 16px;
  flex-shrink: 0;
  color: var(--text-muted);
  transition: color 0.15s ease;
}

.sidebar-item:hover {
  background: var(--page-plane);
  color: var(--text-primary);
}

.sidebar-item.active {
  background: var(--series-1-tint);
  color: var(--series-1);
}

.sidebar-item.active .sidebar-icon {
  color: var(--series-1);
}

.sidebar-item.active::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--series-1);
}

/* ---------- Main content ---------- */

.dashboard-main {
  flex: 1;
  min-width: 0;
}

.dashboard-section h2 {
  font-size: 19px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 4px 0 18px;
  padding-bottom: 12px;
  text-align: left;
  border-bottom: 1px solid var(--gridline);
}

.kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 20px;
}

.overview-intro {
  margin: -8px 0 16px;
  text-align: left;
  font-size: 13px;
  color: var(--text-muted);
}

.insight-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.chart-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.chart-row > .viz-card,
.chart-row > .chart-col {
  flex: 1 1 380px;
}

.chart-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 420px;
}

.chart-toolbar {
  display: flex;
  justify-content: flex-end;
}

/* ---------- Browse-logbooks table ---------- */

.logbook-table-card {
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 18px 20px 20px;
  margin-top: 16px;
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
}

.logbook-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.subsection-heading {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.logbook-search-input {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
  min-width: 240px;
}

.viz-note {
  font-size: 12px;
  color: var(--text-muted);
}

.text-critical {
  color: var(--status-critical);
  font-weight: 600;
}

.status-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-chip.tone-good {
  background: rgba(12, 163, 12, 0.12);
  color: var(--status-good);
}

.status-chip.tone-neutral {
  background: rgba(137, 135, 129, 0.14);
  color: var(--text-secondary);
}

.status-chip.tone-critical {
  background: rgba(208, 59, 59, 0.12);
  color: var(--status-critical);
}

.status-chip.tone-warning {
  background: rgba(250, 178, 25, 0.16);
  color: #a06c00;
}

.session-progress-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.session-select {
  max-width: 220px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
}

.view-icon {
  cursor: pointer;
  color: var(--text-secondary);
  transition: color 0.15s ease;
}

.view-icon:hover {
  color: var(--series-1);
}

/* ---------- Tab transition ---------- */

.tab-fade-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.tab-fade-enter-from {
  opacity: 0;
  transform: translateY(4px);
}

.tab-fade-leave-active,
.tab-fade-leave-to {
  display: none;
}

/* ---------- Responsive ---------- */

@media (max-width: 880px) {
  .dashboard-body {
    flex-direction: column;
    padding: 0 16px;
  }

  .dashboard-sidebar {
    flex: 1 1 auto;
    width: 100%;
    position: static;
    flex-direction: row;
    overflow-x: auto;
    gap: 4px;
  }

  .sidebar-item {
    flex-shrink: 0;
  }

  .sidebar-item.active::before {
    left: 8px;
    right: 8px;
    top: auto;
    bottom: -2px;
    width: auto;
    height: 3px;
    border-radius: 3px 3px 0 0;
  }
}
</style>